import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronRight, Users, Calendar, Image, Crown, ClipboardList,
  CheckCircle2, XCircle, Clock, Briefcase, Radio, ExternalLink,
  Phone, Link2, ArrowRight, Share2, Activity, Search, RefreshCw,
  Zap, Trash2, X, ShieldAlert, Shield, Monitor, Keyboard, DollarSign,
  HardDrive, Download, Cpu, IndianRupee, ChevronDown,
  BarChart3, TrendingUp, CalendarDays, CloudCog, Gauge,
  MessageCircle, Eye, EyeOff, Wallet,
  Palette, Upload, Info, Check, PlayCircle, FlaskConical, Send, Mail,
  FileX,
} from 'lucide-react';
import { Modal } from '../components/UI/Modal';
import toast from 'react-hot-toast';
import { adminApi, AdminUser, AdminEvent, AdminWaitlistEntry, AdminCostBreakdown, AwsBillingData, AdminBlockedIP, AdminDefaultBranding, AdminDefaultBrandingConfig, OverlayAnchor, OverlaySize, AdminAffectedAlbum, AdminAffectedAlbumsDiagnostics, AdminUploadError } from '../api/admin';
import { twilioApi, TwilioStatsResponse, TwilioMessageRow, TwilioChannelFilter } from '../api/twilio';
import { AdminDevToolsLog, QuotaRequest, QuotaType } from '../types';
import { Badge } from '../components/UI/Badge';
import { Button } from '../components/UI/Button';
import { MachhaCharacter } from '../lib/machhaAsset';
import { clayStaggerContainer, clayStaggerItem, backdropVariants, clayModalVariants } from '../constants/animations';
import { usePageTitle } from '../hooks/usePageTitle';
import styles from './AdminPage.module.css';

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

const formatRelative = (date: Date) => {
  const secs = Math.floor((Date.now() - date.getTime()) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  return `${Math.floor(mins / 60)}h ago`;
};

const isHighVolume = (eventsPerMonth: string) => {
  if (eventsPerMonth.includes('30+')) return true;
  const n = parseInt(eventsPerMonth, 10);
  return !isNaN(n) && n >= 30;
};

// ─── UserProfileModal ────────────────────────────────────────────────────────

const UserProfileModal: React.FC<{ user: AdminUser | null; isOpen: boolean; onClose: () => void }> = ({ user, isOpen, onClose }) => {
  const [events, setEvents] = useState<AdminEvent[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [showFullImage, setShowFullImage] = useState(false);

  useEffect(() => {
    if (!user || !isOpen) return;
    if (fetched) return;
    setLoadingEvents(true);
    adminApi.getUserEvents(user._id)
      .then((data) => { setEvents(data); setFetched(true); })
      .catch(() => toast.error('Failed to load events'))
      .finally(() => setLoadingEvents(false));
  }, [user?._id, isOpen, fetched]);

  // Reset when user changes
  useEffect(() => { setFetched(false); setEvents([]); }, [user?._id]);

  if (!user) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={styles['profile-overlay']}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
          {/* Backdrop */}
          <motion.div
            className={styles['profile-backdrop']}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Card */}
          <motion.div
            className={styles['profile-card']}
            initial={{ scale: 0.85, opacity: 0, y: 30 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: 'spring', stiffness: 400, damping: 28 }}
          >
            {/* Gradient banner */}
            <div className={styles['profile-banner']}>
              <button
                onClick={onClose}
                className={styles['profile-close-btn']}
              >
                <X className={styles['profile-close-icon']} />
              </button>
            </div>

            {/* Avatar — overlaps banner, click for fullscreen */}
            <div className={styles['profile-avatar-row']}>
              <motion.div
                className={user.picture ? styles['profile-avatar-clickable'] : styles['profile-avatar']}
                initial={{ scale: 0, rotate: -15 }}
                animate={{ scale: 1, rotate: 0 }}
                whileHover={user.picture ? { scale: 1.08 } : undefined}
                whileTap={user.picture ? { scale: 0.95 } : undefined}
                transition={{ type: 'spring', stiffness: 350, damping: 22, delay: 0.1 }}
                onClick={() => { if (user.picture) setShowFullImage(true); }}
              >
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className={styles['avatar-img']} referrerPolicy="no-referrer" />
                ) : (
                  <div className={styles['profile-avatar-fallback']}>
                    {user.name.charAt(0).toUpperCase()}
                  </div>
                )}
              </motion.div>
            </div>

            {/* User info */}
            <motion.div
              className={styles['profile-info']}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.2 }}
            >
              <div className={styles['profile-name-row']}>
                <h3 className={styles['profile-name']}>{user.name}</h3>
                {user.isOwner && (
                  <span className={styles['owner-badge']}>
                    <Crown className={styles['owner-badge-icon']} />Owner
                  </span>
                )}
              </div>
              <p className={styles['profile-email']}>{user.email}</p>
            </motion.div>

            {/* Stats */}
            <motion.div
              className={styles['profile-stats']}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.2 }}
            >
              <div className={styles['profile-stat-card']}>
                <Calendar className={`${styles['profile-stat-icon']} ${styles['icon-indigo']}`} />
                <span className={styles['profile-stat-value']}>{user.eventCount}</span>
                <span className={styles['profile-stat-label']}>Events</span>
              </div>
              <div className={styles['profile-stat-card']}>
                <Clock className={`${styles['profile-stat-icon']} ${styles['icon-orange']}`} />
                <span className={styles['profile-stat-value-sm']}>{formatDate(user.createdAt)}</span>
                <span className={styles['profile-stat-label']}>Joined</span>
              </div>
            </motion.div>

            {/* Events list */}
            <motion.div
              className={styles['profile-events']}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.25, duration: 0.2 }}
            >
              <p className={styles['section-label']}>Events</p>
              {loadingEvents ? (
                <div className={styles['profile-events-loading']}>
                  <MachhaCharacter state="Loading" part="face" size={28} />
                  <span className={styles['profile-events-loading-text']}>Loading…</span>
                </div>
              ) : events.length === 0 ? (
                <p className={styles['profile-events-empty']}>No events yet.</p>
              ) : (
                <div className={styles['profile-events-list']}>
                  {events.map((event) => (
                    <div
                      key={event._id}
                      className={styles['profile-event-row']}
                    >
                      <div className={styles['flex-1-min-w-0']}>
                        <p className={styles['profile-event-name']}>{event.name}</p>
                      </div>
                      <span className={styles['profile-event-photo-count']}>
                        <Image className={styles['profile-event-photo-icon']} />
                        {event.photoCount}
                      </span>
                      <Badge variant={event.status === 'active' ? 'green' : 'red'}>{event.status}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </motion.div>
        </motion.div>
      )}

      {/* Fullscreen image viewer */}
      {showFullImage && user?.picture && (
        <motion.div
          className={styles['fullscreen-viewer']}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          onClick={() => setShowFullImage(false)}
        >
          <motion.img
            src={user.picture}
            alt={user.name}
            referrerPolicy="no-referrer"
            className={styles['fullscreen-image']}
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.5, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          />
          <button
            onClick={() => setShowFullImage(false)}
            className={styles['fullscreen-close']}
          >
            <X className={styles['fullscreen-close-icon']} />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// ─── UserRow ────────────────────────────────────────────────────────────────

const UserRow: React.FC<{ user: AdminUser; onAvatarClick: () => void; onDeleted: () => void }> = ({ user, onAvatarClick, onDeleted }) => {
  const [expanded, setExpanded] = useState(false);
  const [events, setEvents] = useState<AdminEvent[]>([]);
  const [albums, setAlbums] = useState<import('../api/admin').AdminUserAlbumEntry[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [wiping, setWiping] = useState(false);
  const [wipeConfirmOpen, setWipeConfirmOpen] = useState(false);
  const [wipeEmailInput, setWipeEmailInput] = useState('');

  const handleExpand = async () => {
    if (!expanded && !fetched) {
      setLoadingEvents(true);
      try {
        const [eventsData, albumsData] = await Promise.all([
          adminApi.getUserEvents(user._id),
          adminApi.getUserAlbums(user._id),
        ]);
        setEvents(eventsData);
        setAlbums(albumsData.albums);
        setFetched(true);
      } finally {
        setLoadingEvents(false);
      }
    }
    setExpanded((v) => !v);
  };

  const handleWipe = async () => {
    if (wiping) return;
    if (wipeEmailInput.trim().toLowerCase() !== user.email.toLowerCase()) {
      toast.error('Email does not match');
      return;
    }
    setWiping(true);
    try {
      const result = await adminApi.wipeUserAccount(user._id, wipeEmailInput.trim());
      const total = Object.values(result.deleted).reduce((s, n) => s + n, 0);
      toast.success(`Wiped ${user.email} (${total} records deleted)`);
      onDeleted();
    } catch (err) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        'Failed to wipe account';
      toast.error(msg);
    } finally {
      setWiping(false);
      setWipeConfirmOpen(false);
      setWipeEmailInput('');
    }
  };

  return (
    <motion.div
      variants={clayStaggerItem}
      className={styles['user-row']}
    >
      <button
        className={styles['user-row-btn']}
        onClick={handleExpand}
      >
        <motion.div
          className={styles['user-row-avatar']}
          onClick={(e) => { e.stopPropagation(); onAvatarClick(); }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          transition={{ type: 'spring', stiffness: 500, damping: 20 }}
        >
          {user.picture ? (
            <img src={user.picture} alt={user.name} className={styles['avatar-img']} referrerPolicy="no-referrer" />
          ) : (
            <div className={styles['avatar-fallback-sm']}>
              {user.name.charAt(0).toUpperCase()}
            </div>
          )}
        </motion.div>
        <div className={styles['flex-1-min-w-0']} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div className={styles['user-row-name-row']}>
            <p className={styles['user-row-name']}>{user.name}</p>
            {user.isOwner && (
              <span className={styles['owner-badge']}>
                <Crown className={styles['owner-badge-icon']} />Owner
              </span>
            )}
            {user.betaApproved ? (
              <Badge variant="green">Beta</Badge>
            ) : (
              <Badge variant="yellow">No access</Badge>
            )}
          </div>
          <p className={styles['user-row-email']}>{user.email}</p>
          <p className={styles['user-row-email']} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {user.phone ? (
              <>
                <Phone style={{ width: 11, height: 11, flexShrink: 0, opacity: 0.5 }} />
                {user.phone}
                {user.phoneVerifiedAt ? (
                  <span style={{ color: '#16a34a', flexShrink: 0 }}>verified</span>
                ) : (
                  <span style={{ color: '#f59e0b', flexShrink: 0 }}>not verified</span>
                )}
              </>
            ) : (
              <span style={{ opacity: 0.5, fontStyle: 'italic' }}>No phone number</span>
            )}
          </p>
        </div>
        <div className={styles['user-row-meta']}>
          <span className={styles['user-row-meta-item']}>
            <Calendar className={styles['user-row-meta-icon']} />
            {user.eventCount} {user.eventCount === 1 ? 'event' : 'events'}
          </span>
          {user.albumCount > 0 && (
            <span className={styles['user-row-meta-item']}>
              <Image className={styles['user-row-meta-icon']} />
              {user.albumCount} {user.albumCount === 1 ? 'album' : 'albums'}
              {user.kickedCount > 0 && <span style={{ color: '#ef4444', marginLeft: 4 }}>({user.kickedCount} kicked)</span>}
            </span>
          )}
          <span>{formatDate(user.createdAt)}</span>
        </div>
        <motion.div
          animate={{ rotate: expanded ? 90 : 0 }}
          transition={{ type: 'spring', stiffness: 400, damping: 25 }}
          className={styles['user-row-chevron']}
        >
          <ChevronRight className={styles['user-row-chevron-icon']} />
        </motion.div>
      </button>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className={styles['user-row-expanded']}
          >
            <div className={styles['user-row-expanded-inner']}>
              {loadingEvents ? (
                <div className={styles['user-row-events-loading']}>
                  <MachhaCharacter state="Loading" part="face" size={32} />
                  <span className={styles['user-row-events-loading-text']}>Loading events…</span>
                </div>
              ) : events.length === 0 ? (
                <div className={styles['user-row-events-empty']}>
                  <MachhaCharacter state="Empty" part="face" size={32} />
                  <span className={styles['user-row-events-empty-text']}>No events created yet.</span>
                </div>
              ) : (
                <div className={styles['user-row-events-list']}>
                  {events.map((event) => (
                    <div
                      key={event._id}
                      className={styles['user-row-event-item']}
                    >
                      <div className={styles['flex-1-min-w-0']}>
                        <p className={styles['user-row-event-name']}>{event.name}</p>
                        {event.description && (
                          <p className={styles['user-row-event-desc']}>{event.description}</p>
                        )}
                      </div>
                      <div className={styles['user-row-event-actions']}>
                        <span className={styles['user-row-event-photos']}>
                          <Image className={styles['user-row-event-photos-icon']} />
                          {event.photoCount}
                        </span>
                        <Badge variant={event.status === 'active' ? 'green' : 'red'}>{event.status}</Badge>
                        <span className={styles['user-row-event-date']}>
                          {formatDate(event.createdAt)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Albums */}
              {albums.length > 0 && (
                <div style={{ marginTop: '0.75rem' }}>
                  <p style={{ margin: '0 0 0.5rem', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', opacity: 0.5 }}>
                    Album Memberships
                  </p>
                  <div className={styles['member-row-albums-list']}>
                    {albums.map((a, i) => (
                      <div
                        key={a.albumId ?? i}
                        className={a.isKicked ? styles['member-album-item-kicked'] : styles['member-album-item']}
                      >
                        <Image className={styles['member-album-icon']} />
                        <div className={styles['flex-1-min-w-0']}>
                          <p className={styles['member-album-name']}>
                            {a.albumName}
                          </p>
                          {a.eventName && (
                            <p className={styles['member-album-event-name']}>
                              {a.eventName}
                            </p>
                          )}
                        </div>
                        {a.isKicked && <Badge variant="red">Kicked</Badge>}
                        <p className={styles['member-album-date']}>
                          {formatDate(a.joinedAt)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!user.isOwner && (
                <div
                  style={{
                    marginTop: '1rem',
                    padding: '0.875rem',
                    borderRadius: 'var(--radius-clay-sm)',
                    background: 'rgba(239, 68, 68, 0.06)',
                    border: '1px solid rgba(239, 68, 68, 0.2)',
                  }}
                >
                  {!wipeConfirmOpen ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
                      <div>
                        <p style={{ margin: 0, fontSize: '0.8125rem', fontWeight: 600, color: 'var(--clay-orange)' }}>
                          Danger zone
                        </p>
                        <p style={{ margin: '0.125rem 0 0', fontSize: '0.75rem', color: 'var(--text-disabled)' }}>
                          Permanently delete this user and all their data (events, albums, photos, waitlist, etc.). Cannot be undone.
                        </p>
                      </div>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => setWipeConfirmOpen(true)}
                      >
                        <Trash2 size={14} style={{ marginRight: 4 }} />
                        Wipe account
                      </Button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--text-primary)' }}>
                        Type <strong>{user.email}</strong> to confirm. This will delete <strong>everything</strong> associated with this account.
                      </p>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <input
                          type="email"
                          value={wipeEmailInput}
                          onChange={(e) => setWipeEmailInput(e.target.value)}
                          placeholder={user.email}
                          style={{
                            flex: 1,
                            minWidth: 220,
                            padding: '0.5rem 0.75rem',
                            fontSize: '0.8125rem',
                            borderRadius: 'var(--radius-clay-sm)',
                            border: '1px solid var(--border-subtle)',
                            background: 'var(--bg-surface)',
                            color: 'var(--text-primary)',
                          }}
                          disabled={wiping}
                          autoFocus
                        />
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={handleWipe}
                          loading={wiping}
                          disabled={wiping || wipeEmailInput.trim().toLowerCase() !== user.email.toLowerCase()}
                        >
                          Confirm wipe
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            setWipeConfirmOpen(false);
                            setWipeEmailInput('');
                          }}
                          disabled={wiping}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ─── Status maps ────────────────────────────────────────────────────────────

const STATUS_BADGE_STYLES: Record<string, string> = {
  pending: styles['status-badge-pending'],
  approved: styles['status-badge-approved'],
  rejected: styles['status-badge-rejected'],
};
const STATUS_STRIP_MAP: Record<string, string> = {
  pending: 'waitlist-entry-strip-pending',
  approved: 'waitlist-entry-strip-approved',
  rejected: 'waitlist-entry-strip-rejected',
};
const STATUS_ICON: Record<string, React.ReactNode> = {
  pending: <Clock className={styles['icon-xs']} />,
  approved: <CheckCircle2 className={styles['icon-xs']} />,
  rejected: <XCircle className={styles['icon-xs']} />,
};

// ─── ConfirmModal ────────────────────────────────────────────────────────────

interface ConfirmModalProps {
  isOpen: boolean;
  icon: React.ReactNode;
  iconClass: string;
  title: string;
  message: React.ReactNode;
  confirmLabel: string;
  confirmClass: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen, icon, iconClass, title, message, confirmLabel, confirmClass, onConfirm, onCancel,
}) => (
  <AnimatePresence>
    {isOpen && (
      <motion.div
        className={styles['confirm-overlay']}
        variants={backdropVariants}
        initial="hidden"
        animate="visible"
        exit="hidden"
      >
        <div className={styles['confirm-backdrop']} onClick={onCancel} />
        <motion.div
          className={styles['confirm-card']}
          variants={clayModalVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          <div className={styles['confirm-header']}>
            <div className={`${styles['icon-box-lg']} ${iconClass}`}>
              {icon}
            </div>
            <div>
              <p className={styles['confirm-title']}>{title}</p>
              <p className={styles['confirm-message']}>{message}</p>
            </div>
          </div>
          <div className={styles['confirm-actions']}>
            <button
              onClick={onCancel}
              className={styles['confirm-cancel-btn']}
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className={`${styles['confirm-action-btn']} ${confirmClass}`}
            >
              {confirmLabel}
            </button>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

// ─── WaitlistTab ─────────────────────────────────────────────────────────────

type FilterStatus = 'all' | 'pending' | 'approved' | 'rejected';

const WaitlistTab: React.FC<{ onLoad?: (pending: number) => void }> = ({ onLoad }) => {
  const [entries, setEntries] = useState<AdminWaitlistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [approving, setApproving] = useState<string | null>(null);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AdminWaitlistEntry | null>(null);
  const [rejectTarget, setRejectTarget] = useState<AdminWaitlistEntry | null>(null);
  const [approvedEmail, setApprovedEmail] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const loadData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const data = await adminApi.listWaitlist();
      setEntries(data);
      setLastRefreshed(new Date());
      onLoad?.(data.filter((e) => e.status === 'pending').length);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [onLoad]);

  useEffect(() => { loadData(); }, [loadData]);

  const stats = useMemo(() => ({
    pending: entries.filter((e) => e.status === 'pending').length,
    approved: entries.filter((e) => e.status === 'approved').length,
    rejected: entries.filter((e) => e.status === 'rejected').length,
  }), [entries]);

  const filteredEntries = useMemo(() =>
    entries.filter((e) => {
      if (filterStatus !== 'all' && e.status !== filterStatus) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        return e.user.name.toLowerCase().includes(q) || e.user.email.toLowerCase().includes(q);
      }
      return true;
    }),
    [entries, filterStatus, searchQuery]
  );

  const pendingInFilter = useMemo(() => filteredEntries.filter((e) => e.status === 'pending'), [filteredEntries]);
  const allPendingSelected = pendingInFilter.length > 0 && pendingInFilter.every((e) => selectedIds.has(e._id));
  const selectedPendingCount = useMemo(() =>
    [...selectedIds].filter((id) => entries.find((e) => e._id === id)?.status === 'pending').length,
    [selectedIds, entries]
  );

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });
  };

  const toggleSelectAllPending = () => {
    if (allPendingSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        pendingInFilter.forEach((e) => next.delete(e._id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        pendingInFilter.forEach((e) => next.add(e._id));
        return next;
      });
    }
  };

  const handleCopyLink = async () => {
    await navigator.clipboard.writeText(window.location.origin);
    setLinkCopied(true);
    toast.success('App link copied!');
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const handleApprove = async (entry: AdminWaitlistEntry) => {
    setApproving(entry._id);
    try {
      const res = await adminApi.updateWaitlistStatus(entry._id, 'approved');
      setEntries((prev) => prev.map((e) => e._id === entry._id ? { ...e, status: 'approved' as const } : e));
      setApprovedEmail(entry.user.email);
      toast.success(`${entry.user.name} approved${res.emailSent ? ' — email sent' : ''}.`);
      if (!res.emailSent) {
        toast.error(`Approval email failed: ${res.emailError ?? 'unknown error'}`);
      }
    } catch {
      toast.error('Failed to update status');
    } finally {
      setApproving(null);
    }
  };

  const confirmReject = async () => {
    if (!rejectTarget) return;
    const entry = rejectTarget;
    setRejectTarget(null);
    setApproving(entry._id);
    try {
      const res = await adminApi.updateWaitlistStatus(entry._id, 'rejected');
      setEntries((prev) => prev.map((e) => e._id === entry._id ? { ...e, status: 'rejected' as const } : e));
      toast.success(`${entry.user.name} rejected${res.emailSent ? ' — email sent' : ''}.`);
      if (!res.emailSent) {
        toast.error(`Rejection email failed: ${res.emailError ?? 'unknown error'}`);
      }
    } catch {
      toast.error('Failed to update status');
    } finally {
      setApproving(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const entry = deleteTarget;
    setDeleteTarget(null);
    setApproving(entry._id);
    try {
      await adminApi.deleteWaitlistEntry(entry._id);
      setEntries((prev) => prev.filter((e) => e._id !== entry._id));
      setSelectedIds((prev) => { const next = new Set(prev); next.delete(entry._id); return next; });
      toast.success(`${entry.user.name}'s entry deleted.`);
    } catch {
      toast.error('Failed to delete entry');
    } finally {
      setApproving(null);
    }
  };

  const handleBulkAction = async (status: 'approved' | 'rejected') => {
    const ids = [...selectedIds].filter((id) => entries.find((e) => e._id === id)?.status === 'pending');
    if (ids.length === 0) return;
    setBulkProcessing(true);
    try {
      const results = await Promise.all(ids.map((id) => adminApi.updateWaitlistStatus(id, status)));
      setEntries((prev) => prev.map((e) => ids.includes(e._id) ? { ...e, status } : e));
      setSelectedIds(new Set());
      const emailFailures = results.filter((r) => !r.emailSent).length;
      const verb = status === 'approved' ? 'approved' : 'rejected';
      if (status === 'approved') {
        const lastEntry = entries.find((e) => e._id === ids[ids.length - 1]);
        if (lastEntry) setApprovedEmail(lastEntry.user.email);
      }
      toast.success(`${ids.length} applicant${ids.length > 1 ? 's' : ''} ${verb}.`);
      if (emailFailures > 0) {
        toast.error(`${emailFailures} ${verb === 'approved' ? 'approval' : 'rejection'} email${emailFailures > 1 ? 's' : ''} failed to send.`);
      }
    } catch {
      toast.error('Bulk action failed — refreshing…');
      loadData(true);
    } finally {
      setBulkProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className={styles['loading-wrapper']}>
        <motion.p
          className={styles['loading-text']}
          animate={{ opacity: [0.4, 1, 0.4] }}
          transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
        >
          Loading waitlist…
        </motion.p>
        <motion.div
          className={styles['loading-mascot']}
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: [6, -4, 6], opacity: 1 }}
          transition={{ y: { repeat: Infinity, duration: 2.4, ease: 'easeInOut' }, opacity: { duration: 0.4 } }}
        >
          <MachhaCharacter state="Loading" part="face" size={100} />
        </motion.div>
      </div>
    );
  }

  return (
    <div>
      {/* Google OAuth reminder banner */}
      <AnimatePresence>
        {approvedEmail && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className={styles['waitlist-reminder']}
          >
            <ExternalLink className={styles['waitlist-reminder-icon']} />
            <span>
              <strong>Remember:</strong> Add <strong>{approvedEmail}</strong> to{' '}
              <span className={styles['underline-hint']}>Google Cloud Console → OAuth consent screen → Test users</span>{' '}
              while the app is in Testing mode.
            </span>
            <button onClick={() => setApprovedEmail(null)} className={styles['waitlist-reminder-close']}>
              <X className={styles['waitlist-reminder-close-icon']} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Filter pills + refresh */}
      <div className={styles['waitlist-filters']}>
        {(['all', 'pending', 'approved', 'rejected'] as const).map((s) => {
          const count = s === 'all' ? entries.length : stats[s];
          const active = filterStatus === s;
          const colors: Record<FilterStatus, string> = {
            all: active ? styles['filter-all-active'] : styles['filter-all-inactive'],
            pending: active ? styles['filter-pending-active'] : styles['filter-pending-inactive'],
            approved: active ? styles['filter-approved-active'] : styles['filter-approved-inactive'],
            rejected: active ? styles['filter-rejected-active'] : styles['filter-rejected-inactive'],
          };
          return (
            <button
              key={s}
              onClick={() => { setFilterStatus(s); setSelectedIds(new Set()); }}
              className={`${styles['waitlist-filter-pill']} ${colors[s]}`}
            >
              {s === 'pending' && <Clock className={styles['waitlist-filter-pill-icon']} />}
              {s === 'approved' && <CheckCircle2 className={styles['waitlist-filter-pill-icon']} />}
              {s === 'rejected' && <XCircle className={styles['waitlist-filter-pill-icon']} />}
              {s === 'all' ? `All (${count})` : `${s.charAt(0).toUpperCase() + s.slice(1)} (${count})`}
            </button>
          );
        })}

        <div className={styles['waitlist-refresh-area']}>
          {lastRefreshed && (
            <span className={styles['waitlist-refresh-time']}>
              Updated {formatRelative(lastRefreshed)}
            </span>
          )}
          <button
            onClick={() => loadData(true)}
            disabled={refreshing}
            title="Refresh"
            className={styles['waitlist-refresh-btn']}
          >
            <motion.div
              animate={refreshing ? { rotate: 360 } : { rotate: 0 }}
              transition={{ duration: 0.8, repeat: refreshing ? Infinity : 0, ease: 'linear' }}
            >
              <RefreshCw className={styles['waitlist-refresh-icon']} />
            </motion.div>
          </button>
        </div>
      </div>

      {entries.length === 0 ? (
        /* ── Empty state ── */
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className={styles['space-y-4']}
        >
          <div className={`${styles['card-clay-lg']} ${styles['waitlist-empty-card']}`}>
            <div className={styles['flex-shrink-0']}>
              <MachhaCharacter state="Onboarding" part="face" size={64} />
            </div>
            <div>
              <p className={styles['waitlist-empty-title']}>
                Hey Admin! Your waitlist is empty.
              </p>
              <p className={styles['waitlist-empty-desc']}>
                Share the app link so users can sign in and apply. Once they do, you'll see their applications here and can approve or reject them.
              </p>
            </div>
          </div>

          <div className={`${styles['card-clay']} ${styles['waitlist-how-it-works']}`}>
            <p className={styles['section-label']}>How it works</p>
            <div className={styles['waitlist-hiw-row']}>
              {([
                { icon: Share2, label: 'Share your app link', color: styles['hiw-color-indigo'] },
                { icon: ClipboardList, label: 'Users sign in & apply', color: styles['hiw-color-orange'] },
                { icon: CheckCircle2, label: 'You review & approve', color: styles['hiw-color-mint'] },
              ] as const).map((step, i) => (
                <React.Fragment key={i}>
                  <div className={styles['waitlist-hiw-step']}>
                    <div className={`${styles['icon-box-sm']} ${step.color}`}>
                      <step.icon className={styles['waitlist-hiw-icon']} />
                    </div>
                    <span className={styles['waitlist-hiw-label']}>{step.label}</span>
                  </div>
                  {i < 2 && <ArrowRight className={styles['waitlist-hiw-arrow']} />}
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className={styles['waitlist-cards-grid']}>
            <div className={`${styles['card-clay']} ${styles['waitlist-card-inner']}`}>
              <div className={styles['waitlist-card-header']}>
                <div className={`${styles['icon-box-sm']} ${styles['icon-box-bg-indigo']}`}>
                  <Link2 className={[styles['icon-sm'], styles['icon-indigo']].join(' ')} />
                </div>
                <p className={styles['waitlist-card-title']}>App Link</p>
              </div>
              <p className={styles['waitlist-link-url']}>
                {window.location.origin}
              </p>
              <button
                onClick={handleCopyLink}
                className={styles['waitlist-copy-btn']}
              >
                {linkCopied ? <CheckCircle2 className={styles['waitlist-copy-icon']} /> : <Link2 className={styles['waitlist-copy-icon']} />}
                {linkCopied ? 'Copied!' : 'Copy Link'}
              </button>
            </div>

            <div className={`${styles['card-clay']} ${styles['waitlist-card-inner']}`}>
              <div className={styles['waitlist-card-header']}>
                <div className={`${styles['icon-box-sm']} ${styles['icon-box-bg-mint']}`}>
                  <Activity className={[styles['icon-sm'], styles['icon-mint']].join(' ')} />
                </div>
                <p className={styles['waitlist-card-title']}>System Status</p>
              </div>
              <div className={styles['space-y-2']}>
                {(['Waitlist', 'Google OAuth', 'Approval flow'] as const).map((label) => (
                  <div key={label} className={styles['waitlist-status-row']}>
                    <span className={styles['waitlist-status-label']}>{label}</span>
                    <span className={styles['waitlist-status-value']}>
                      <span className={styles['waitlist-status-dot']} />
                      Ready
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      ) : (
        <>
          {/* Search */}
          <div className={styles['waitlist-search-wrap']}>
            <Search className={styles['waitlist-search-icon']} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name or email…"
              className={styles['waitlist-search-input']}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className={styles['waitlist-search-clear']}
              >
                <X className={styles['waitlist-search-clear-icon']} />
              </button>
            )}
          </div>

          {filteredEntries.length === 0 ? (
            <div className={styles['waitlist-no-match']}>
              <MachhaCharacter state="Empty" part="face" size={56} />
              <p className={styles['waitlist-no-match-text']}>No entries match your filter.</p>
              <button
                onClick={() => { setFilterStatus('all'); setSearchQuery(''); }}
                className={styles['waitlist-clear-link']}
              >
                Clear filters
              </button>
            </div>
          ) : (
            <>
              {/* Bulk select header */}
              {pendingInFilter.length > 0 && (
                <div className={styles['waitlist-bulk-header']}>
                  <button
                    onClick={toggleSelectAllPending}
                    className={styles['waitlist-bulk-toggle']}
                  >
                    <div className={allPendingSelected ? styles['waitlist-checkbox-checked'] : styles['waitlist-checkbox']}>
                      {allPendingSelected && <CheckCircle2 className={styles['waitlist-checkbox-icon']} />}
                    </div>
                    {allPendingSelected
                      ? 'Deselect all pending'
                      : `Select all pending (${pendingInFilter.length})`}
                  </button>
                  {selectedIds.size > 0 && (
                    <span className={styles['waitlist-bulk-hint']}>
                      {selectedPendingCount} selected · use bar below to act
                    </span>
                  )}
                </div>
              )}

              {/* Entry rows */}
              <motion.div
                className={styles['space-y-2']}
                variants={clayStaggerContainer}
                initial="hidden"
                animate="visible"
              >
                {filteredEntries.map((entry) => {
                  const isSelected = selectedIds.has(entry._id);
                  const isBusy = approving === entry._id || bulkProcessing;
                  const highVol = isHighVolume(entry.answers.eventsPerMonth);

                  return (
                    <motion.div
                      key={entry._id}
                      variants={clayStaggerItem}
                      className={isSelected ? styles['waitlist-entry-selected'] : styles['waitlist-entry']}
                    >
                      <div className={styles['waitlist-entry-content']}>
                        {/* Left status strip */}
                        <div className={styles[STATUS_STRIP_MAP[entry.status]]} />

                        {/* Content */}
                        <div className={styles['waitlist-entry-body']}>
                          {/* Primary row: avatar + checkbox + name + actions */}
                          <div className={styles['waitlist-entry-primary']}>
                            {/* Avatar */}
                            <div className={styles['avatar-circle-sm']}>
                              {entry.user.picture ? (
                                <img
                                  src={entry.user.picture}
                                  alt={entry.user.name}
                                  className={styles['avatar-img']}
                                  referrerPolicy="no-referrer"
                                />
                              ) : (
                                <div className={styles['avatar-fallback-sm']}>
                                  {entry.user.name.charAt(0).toUpperCase()}
                                </div>
                              )}
                            </div>

                            {/* Checkbox (pending only) */}
                            {entry.status === 'pending' && (
                              <div className={styles['waitlist-entry-checkbox-col']}>
                                <button onClick={() => toggleSelect(entry._id)} className={styles['waitlist-entry-checkbox-btn']}>
                                  <div className={isSelected ? styles['waitlist-checkbox-checked'] : styles['waitlist-checkbox']}>
                                    {isSelected && <CheckCircle2 className={styles['waitlist-checkbox-icon']} />}
                                  </div>
                                </button>
                              </div>
                            )}

                            {/* Name + email + phone + tags */}
                            <div className={styles['waitlist-entry-name-area']} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                              <div className={styles['waitlist-entry-name-row']}>
                                <span className={styles['waitlist-entry-name']}>
                                  {entry.user.name}
                                </span>
                                {entry.user.isOwner && (
                                  <span className={styles['owner-badge']}>
                                    <Crown className={styles['owner-badge-icon']} />Owner
                                  </span>
                                )}
                                {highVol && (
                                  <span className={styles['high-volume-badge']}>
                                    <Zap className={styles['high-volume-icon']} />High Volume
                                  </span>
                                )}
                                <span className={`${styles['status-badge']} ${STATUS_BADGE_STYLES[entry.status]}`}>
                                  {STATUS_ICON[entry.status]}
                                  {entry.status}
                                </span>
                              </div>
                              <p className={styles['waitlist-entry-email']}>
                                {entry.user.email}
                              </p>
                            </div>

                            {/* Action buttons */}
                            <div className={styles['waitlist-entry-actions']}>
                              {entry.status === 'pending' && (
                                <>
                                  <div>
                                    <Button
                                      variant="success"
                                      size="sm"
                                      disabled={isBusy}
                                      loading={approving === entry._id}
                                      onClick={() => handleApprove(entry)}
                                    >
                                      <CheckCircle2 className={styles['icon-sm']} />
                                      Approve
                                    </Button>
                                  </div>
                                  <div>
                                    <Button
                                      variant="danger"
                                      size="sm"
                                      disabled={isBusy}
                                      onClick={() => setRejectTarget(entry)}
                                    >
                                      <XCircle className={styles['icon-sm']} />
                                      Reject
                                    </Button>
                                  </div>
                                </>
                              )}
                              <button
                                disabled={isBusy}
                                onClick={() => setDeleteTarget(entry)}
                                title="Delete entry"
                                className={styles['waitlist-delete-btn']}
                              >
                                <Trash2 className={styles['waitlist-delete-icon']} />
                              </button>
                            </div>
                          </div>

                          {/* Metadata row */}
                          <div className={styles['waitlist-entry-meta']}>
                            <span className={styles['waitlist-entry-meta-item']}>
                              <Briefcase className={styles['waitlist-entry-meta-icon']} />{entry.answers.role}
                            </span>
                            <span className={styles['waitlist-entry-meta-item']}>
                              <Calendar className={styles['waitlist-entry-meta-icon']} />{entry.answers.eventsPerMonth}/mo
                            </span>
                            <span className={styles['waitlist-entry-meta-item']}>
                              <Users className={styles['waitlist-entry-meta-icon']} />{entry.answers.teamSize}
                            </span>
                            <span className={styles['waitlist-entry-meta-item']}>
                              <Radio className={styles['waitlist-entry-meta-icon']} />{entry.answers.howHeard}
                            </span>
                          </div>

                          {/* Date row */}
                          <p className={styles['waitlist-entry-date']}>
                            Applied {formatDate(entry.submittedAt)}
                            {entry.reviewedAt && ` · Reviewed ${formatDate(entry.reviewedAt)}`}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </motion.div>
            </>
          )}
        </>
      )}

      {/* Bulk action floating bar */}
      <AnimatePresence>
        {selectedIds.size > 0 && (
          <motion.div
            initial={{ y: 24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 24, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 420, damping: 30 }}
            className={styles['bulk-bar']}
          >
            <span className={styles['bulk-bar-label']}>
              {selectedPendingCount} pending selected
            </span>
            <div className={styles['bulk-bar-divider']} />
            <button
              disabled={bulkProcessing || selectedPendingCount === 0}
              onClick={() => handleBulkAction('approved')}
              className={styles['bulk-approve-btn']}
            >
              {bulkProcessing ? '…' : `Approve ${selectedPendingCount}`}
            </button>
            <button
              disabled={bulkProcessing || selectedPendingCount === 0}
              onClick={() => handleBulkAction('rejected')}
              className={styles['bulk-reject-btn']}
            >
              {bulkProcessing ? '…' : `Reject ${selectedPendingCount}`}
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              className={styles['bulk-close-btn']}
            >
              <X className={styles['bulk-close-icon']} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete confirm modal */}
      <ConfirmModal
        isOpen={!!deleteTarget}
        icon={<Trash2 className={styles['icon-md']} />}
        iconClass={styles['confirm-icon-rose']}
        title={`Delete ${deleteTarget?.user.name}'s entry?`}
        message="This permanently removes their waitlist entry and resets their access. They can re-apply."
        confirmLabel="Delete"
        confirmClass={styles['confirm-btn-rose']}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* Reject confirm modal */}
      <ConfirmModal
        isOpen={!!rejectTarget}
        icon={<XCircle className={styles['icon-md']} />}
        iconClass={styles['confirm-icon-rose']}
        title={`Reject ${rejectTarget?.user.name}?`}
        message="They won't receive app access. You can approve them later if their situation changes."
        confirmLabel="Reject"
        confirmClass={styles['confirm-btn-rose']}
        onConfirm={confirmReject}
        onCancel={() => setRejectTarget(null)}
      />
    </div>
  );
};

// ─── DevToolsTab ─────────────────────────────────────────────────────────────

const DevToolsTab: React.FC = () => {
  const [logs, setLogs] = useState<AdminDevToolsLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    adminApi.listDevToolsLogs(1).then((r) => {
      setLogs(r.logs);
      setTotalPages(r.pagination.pages);
      setTotal(r.pagination.total);
      setPage(1);
    }).catch(() => toast.error('Failed to load devtools logs')).finally(() => setLoading(false));
  }, []);

  const loadMore = async () => {
    if (loadingMore || page >= totalPages) return;
    setLoadingMore(true);
    const next = page + 1;
    try {
      const r = await adminApi.listDevToolsLogs(next);
      setLogs((prev) => [...prev, ...r.logs]);
      setPage(next);
      setTotalPages(r.pagination.pages);
    } catch {
      toast.error('Failed to load more');
    } finally {
      setLoadingMore(false);
    }
  };

  if (loading) {
    return (
      <div className={styles['loading-wrapper']}>
        <motion.p
          className={styles['loading-text']}
          animate={{ opacity: [0.4, 1, 0.4] }}
          transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
        >
          Loading logs…
        </motion.p>
        <motion.div
          className={styles['loading-mascot']}
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: [6, -4, 6], opacity: 1 }}
          transition={{ y: { repeat: Infinity, duration: 2.4, ease: 'easeInOut' }, opacity: { duration: 0.4 } }}
        >
          <MachhaCharacter state="Loading" part="face" size={100} />
        </motion.div>
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className={styles['empty-center']}>
        <MachhaCharacter state="Empty" size={140} />
        <p className={styles['empty-title']}>No devtools events logged</p>
        <p className={styles['devtools-empty-desc']}>
          When a guest opens devtools (F12, Ctrl+Shift+I, or window resize) while viewing an album, it will appear here.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Summary */}
      <div className={styles['devtools-summary']}>
        <div className={styles['devtools-summary-icon-box']}>
          <ShieldAlert className={styles['devtools-summary-icon']} />
        </div>
        <div>
          <p className={styles['devtools-summary-title']}>
            {total} detection{total !== 1 ? 's' : ''} recorded
          </p>
          <p className={styles['devtools-summary-subtitle']}>
            Logged when album viewers open browser devtools
          </p>
        </div>
      </div>

      {/* Log list */}
      <motion.div
        className={styles['space-y-2']}
        variants={clayStaggerContainer}
        initial="hidden"
        animate="visible"
      >
        {logs.map((log) => (
          <motion.div
            key={log._id}
            variants={clayStaggerItem}
            className={`${styles['card-clay']} ${styles['devtools-log-row']}`}
          >
            {/* Method badge */}
            <div className={log.method === 'keyboard' ? styles['devtools-method-keyboard'] : styles['devtools-method-resize']}>
              {log.method === 'keyboard'
                ? <Keyboard className={styles['devtools-method-icon']} />
                : <Monitor className={styles['devtools-method-icon']} />
              }
              {log.method}
            </div>

            {/* User info */}
            <div className={styles['flex-1-min-w-0']}>
              <p className={styles['devtools-user-name']}>
                {log.userId?.name ?? 'Deleted user'}
              </p>
              <p className={styles['devtools-user-email']}>
                {log.userId?.email ?? '—'}
                <span className={styles['devtools-dot']}>·</span>
                album: <span className={styles['devtools-album-label']}>{log.albumId?.name ?? 'Deleted album'}</span>
              </p>
            </div>

            {/* Timestamp */}
            <p className={styles['devtools-date']}>
              {formatDate(log.detectedAt)}
            </p>
          </motion.div>
        ))}
      </motion.div>

      {/* Load More */}
      {page < totalPages && (
        <div className={styles['devtools-load-more']}>
          <button
            onClick={loadMore}
            disabled={loadingMore}
            className={styles['devtools-load-more-btn']}
          >
            {loadingMore ? 'Loading…' : 'Load More'}
          </button>
        </div>
      )}
    </div>
  );
};

// ─── CostsTab ─────────────────────────────────────────────────────────────────

type Currency = 'usd' | 'inr';
type CostView = 'users' | 'events';
type CostMode = 'estimated' | 'billing';
type DatePreset = '7d' | '30d' | '90d' | '6m' | '1y' | 'custom';
type BillingGranularity = 'DAILY' | 'MONTHLY';

const SERVICE_COLORS: Record<string, string> = {
  'Amazon Simple Storage Service': styles['svc-color-teal'],
  'AWS Lambda': styles['svc-color-orange'],
  'Amazon CloudFront': styles['svc-color-sky'],
  'Amazon API Gateway': styles['svc-color-amber'],
  'Amazon DynamoDB': styles['svc-color-rose'],
  'AWS Secrets Manager': styles['svc-color-indigo'],
  'Amazon Simple Notification Service': styles['svc-color-purple'],
  'Amazon Route 53': styles['svc-color-emerald'],
  'AWS CloudTrail': styles['svc-color-pink'],
  'Tax': styles['svc-color-zinc'],
};
const DEFAULT_SERVICE_COLOR = styles['svc-color-slate'];

function getServiceColor(name: string) {
  return SERVICE_COLORS[name] ?? DEFAULT_SERVICE_COLOR;
}

function getServiceShortName(name: string): string {
  const map: Record<string, string> = {
    'Amazon Simple Storage Service': 'S3',
    'AWS Lambda': 'Lambda',
    'Amazon CloudFront': 'CloudFront',
    'Amazon API Gateway': 'API Gateway',
    'Amazon DynamoDB': 'DynamoDB',
    'AWS Secrets Manager': 'Secrets Mgr',
    'Amazon Simple Notification Service': 'SNS',
    'Amazon Route 53': 'Route 53',
    'AWS CloudTrail': 'CloudTrail',
    'Tax': 'Tax',
  };
  return map[name] ?? name.replace(/^(Amazon|AWS)\s+/, '');
}

function getDateRange(preset: DatePreset): { start: string; end: string } {
  const now = new Date();
  const end = now.toISOString().slice(0, 10);
  const ms = (days: number) => new Date(now.getTime() - days * 86400000).toISOString().slice(0, 10);
  switch (preset) {
    case '7d': return { start: ms(7), end };
    case '30d': return { start: ms(30), end };
    case '90d': return { start: ms(90), end };
    case '6m': return { start: ms(180), end };
    case '1y': return { start: ms(365), end };
    default: return { start: ms(30), end };
  }
}

const PRESET_LABELS: Record<DatePreset, string> = {
  '7d': '7 Days',
  '30d': '30 Days',
  '90d': '3 Months',
  '6m': '6 Months',
  '1y': '1 Year',
  'custom': 'Custom',
};

// ─── AWS Billing Sub-Tab ────────────────────────────────────────────────────

const AwsBillingView: React.FC<{ currency: Currency }> = ({ currency }) => {
  const [billing, setBilling] = useState<AwsBillingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [preset, setPreset] = useState<DatePreset>('30d');
  const [granularity, setGranularity] = useState<BillingGranularity>('DAILY');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [expandedService, setExpandedService] = useState<string | null>(null);

  const fetchBilling = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const range = preset === 'custom'
        ? { start: customStart, end: customEnd }
        : getDateRange(preset);
      const data = await adminApi.getAwsBilling({ ...range, granularity });
      setBilling(data);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string; code?: string } } };
      const msg = e.response?.data?.error || 'Failed to load AWS billing';
      const code = e.response?.data?.code || '';
      setError(code ? `${code}: ${msg}` : msg);
    } finally {
      setLoading(false);
    }
  }, [preset, granularity, customStart, customEnd]);

  useEffect(() => {
    if (preset !== 'custom') fetchBilling();
  }, [preset, granularity]); // eslint-disable-line react-hooks/exhaustive-deps

  const fmt = useCallback((usd: number) => {
    if (currency === 'inr') {
      const rate = billing?.exchangeRate ?? 85;
      const inr = usd * rate;
      if (inr < 1 && inr > 0) return `${(inr * 100).toFixed(2)} paise`;
      return `₹${inr.toFixed(2)}`;
    }
    if (usd < 0.01 && usd > 0) return `$${usd.toFixed(4)}`;
    return `$${usd.toFixed(2)}`;
  }, [currency, billing]);

  // Build the daily/monthly cost chart bars
  const maxTimelineTotal = useMemo(
    () => Math.max(...(billing?.timeline.map((t) => t.total) ?? [0]), 0.001),
    [billing],
  );

  return (
    <div>
      {/* Controls row */}
      <div className={styles['costs-controls-row']}>
        {/* Date presets */}
        <div>
          <p className={styles['costs-control-label']}>Period</p>
          <div className={styles['toggle-group']}>
            {(['7d', '30d', '90d', '6m', '1y', 'custom'] as const).map((p) => (
              <button
                key={p}
                onClick={() => setPreset(p)}
                className={preset === p ? styles['costs-toggle-btn-active'] : styles['costs-toggle-btn']}
              >
                {PRESET_LABELS[p]}
              </button>
            ))}
          </div>
        </div>

        {/* Granularity */}
        <div>
          <p className={styles['costs-control-label']}>Granularity</p>
          <div className={styles['toggle-group']}>
            {(['DAILY', 'MONTHLY'] as const).map((g) => (
              <button
                key={g}
                onClick={() => setGranularity(g)}
                className={granularity === g ? styles['costs-toggle-btn-wide-active'] : styles['costs-toggle-btn-wide']}
              >
                {g === 'DAILY' ? 'Daily' : 'Monthly'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Custom date inputs */}
      {preset === 'custom' && (
        <motion.div
          className={styles['costs-custom-dates']}
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
        >
          <div>
            <label className={styles['costs-date-label']}>Start</label>
            <input
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              className={styles['costs-date-input']}
            />
          </div>
          <div>
            <label className={styles['costs-date-label']}>End</label>
            <input
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              className={styles['costs-date-input']}
            />
          </div>
          <button
            onClick={fetchBilling}
            disabled={!customStart || !customEnd}
            className={styles['costs-apply-btn']}
          >
            Apply
          </button>
        </motion.div>
      )}

      {/* Loading */}
      {loading && (
        <div className={styles['loading-wrapper']}>
          <motion.p
            className={styles['loading-text']}
            animate={{ opacity: [0.4, 1, 0.4] }}
            transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
          >
            Fetching AWS billing…
          </motion.p>
          <motion.div
            className={styles['loading-mascot']}
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: [6, -4, 6], opacity: 1 }}
            transition={{ y: { repeat: Infinity, duration: 2.4, ease: 'easeInOut' }, opacity: { duration: 0.4 } }}
          >
            <MachhaCharacter state="Loading" part="face" size={100} />
          </motion.div>
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className={styles['costs-error-center']}>
          <MachhaCharacter state="Error" size={140} />
          <p className={styles['costs-error-title']}>Failed to load AWS billing</p>
          <p className={styles['costs-error-msg']}>
            {error}
          </p>
          <button onClick={fetchBilling} className={styles['costs-retry-btn']}>
            Retry
          </button>
        </div>
      )}

      {/* Billing data */}
      {!loading && !error && billing && (
        <>
          {/* Grand total + date range banner */}
          <motion.div
            className={`${styles['card-clay']} ${styles['costs-total-banner']}`}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className={styles['costs-total-row']}>
              <div>
                <p className={styles['costs-control-label']}>
                  AWS Total Cost
                </p>
                <p className={styles['costs-total-value']}>{fmt(billing.grandTotal)}</p>
              </div>
              <div className={styles['text-right']}>
                <p className={styles['costs-control-label']}>Period</p>
                <p className={styles['costs-period-text']}>
                  <CalendarDays className={styles['costs-period-icon']} />
                  {billing.startDate} → {billing.endDate}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Service breakdown */}
          <div className={styles['mb-6']}>
            <p className={styles['costs-section-label']}>
              Cost by Service
            </p>

            {/* Stacked bar summary */}
            {billing.grandTotal > 0 && (
              <div className={styles['costs-stacked-bar']}>
                {billing.services.map((svc) => (
                  <motion.div
                    key={svc.name}
                    className={`${styles['costs-stacked-segment']} ${getServiceColor(svc.name)}`}
                    initial={{ width: 0 }}
                    animate={{ width: `${svc.percentage}%` }}
                    transition={{ duration: 0.6, ease: 'easeOut' }}
                    title={`${getServiceShortName(svc.name)}: ${fmt(svc.cost)} (${svc.percentage.toFixed(1)}%)`}
                  />
                ))}
              </div>
            )}

            {/* Service rows */}
            <motion.div className={styles['space-y-1-5']} variants={clayStaggerContainer} initial="hidden" animate="visible">
              {billing.services.map((svc) => {
                const isExpanded = expandedService === svc.name;
                const serviceTimeline = billing.timeline
                  .map((t) => ({ start: t.start, end: t.end, cost: t.services[svc.name]?.cost ?? 0 }))
                  .filter((t) => t.cost > 0);

                return (
                  <motion.div
                    key={svc.name}
                    variants={clayStaggerItem}
                    className={styles['card-clay']}
                  >
                    <button
                      className={styles['costs-service-btn']}
                      onClick={() => setExpandedService(isExpanded ? null : svc.name)}
                    >
                      <div className={`${styles['costs-service-dot']} ${getServiceColor(svc.name)}`} />
                      <p className={styles['costs-service-name']}>
                        {getServiceShortName(svc.name)}
                      </p>
                      <p className={styles['costs-service-pct']}>
                        {svc.percentage.toFixed(1)}%
                      </p>
                      <p className={styles['costs-service-cost']}>
                        {fmt(svc.cost)}
                      </p>
                      <motion.div
                        animate={{ rotate: isExpanded ? 180 : 0 }}
                        transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                        className={styles['costs-chevron']}
                      >
                        <ChevronDown className={styles['costs-chevron-icon']} />
                      </motion.div>
                    </button>

                    <AnimatePresence initial={false}>
                      {isExpanded && serviceTimeline.length > 0 && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2, ease: 'easeInOut' }}
                          className={styles['costs-service-expanded']}
                        >
                          <div className={styles['costs-service-expanded-inner']}>
                            <p className={styles['section-label']}>
                              {granularity === 'DAILY' ? 'Daily' : 'Monthly'} Breakdown
                            </p>
                            <div className={styles['costs-timeline-list']}>
                              {serviceTimeline.map((t) => (
                                <div key={t.start} className={styles['costs-timeline-row']}>
                                  <span className={styles['costs-timeline-date']}>
                                    {new Date(t.start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                  </span>
                                  <div className={styles['costs-timeline-bar-bg']}>
                                    <motion.div
                                      className={`${styles['costs-timeline-bar-fill']} ${getServiceColor(svc.name)}`}
                                      initial={{ width: 0 }}
                                      animate={{ width: `${Math.max((t.cost / (Math.max(...serviceTimeline.map(s => s.cost), 0.001))) * 100, 2)}%` }}
                                      transition={{ duration: 0.4, ease: 'easeOut' }}
                                    />
                                  </div>
                                  <span className={styles['costs-timeline-cost']}>
                                    {fmt(t.cost)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>

          {/* Timeline chart (bar chart) */}
          {billing.timeline.length > 0 && (
            <div className={styles['costs-chart-section']}>
              <p className={styles['costs-chart-label']}>
                <BarChart3 className={styles['costs-chart-icon']} />
                {granularity === 'DAILY' ? 'Daily' : 'Monthly'} Spend
              </p>
              <div className={`${styles['card-clay']} ${styles['costs-chart-card']}`}>
                <div className={styles['costs-chart-bars']}>
                  {billing.timeline.map((t, i) => {
                    const heightPct = Math.max((t.total / maxTimelineTotal) * 100, 2);
                    return (
                      <div key={t.start} className={styles['costs-chart-bar-col']}>
                        {/* Tooltip */}
                        <div className={styles['costs-chart-tooltip']}>
                          <div className={styles['costs-chart-tooltip-inner']}>
                            <p className={styles['costs-chart-tooltip-date']}>
                              {new Date(t.start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </p>
                            <p className={styles['costs-chart-tooltip-value']}>{fmt(t.total)}</p>
                          </div>
                        </div>
                        <motion.div
                          className={styles['costs-chart-bar']}
                          initial={{ height: 0 }}
                          animate={{ height: `${heightPct}%` }}
                          transition={{ duration: 0.5, delay: i * 0.01, ease: 'easeOut' }}
                        />
                      </div>
                    );
                  })}
                </div>
                {/* X-axis labels — show a few dates */}
                <div className={styles['costs-chart-x-axis']}>
                  {[0, Math.floor(billing.timeline.length / 2), billing.timeline.length - 1]
                    .filter((v, i, a) => a.indexOf(v) === i && billing.timeline[v])
                    .map((idx) => (
                      <span key={idx} className={styles['costs-chart-x-label']}>
                        {new Date(billing.timeline[idx].start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                    ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ─── Estimated Costs Sub-Tab ────────────────────────────────────────────────

const EstimatedCostsView: React.FC<{ currency: Currency }> = ({ currency }) => {
  const [data, setData] = useState<AdminCostBreakdown | null>(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<CostView>('users');
  const [expandedUser, setExpandedUser] = useState<string | null>(null);
  const [expandedEvent, setExpandedEvent] = useState<string | null>(null);

  useEffect(() => {
    adminApi.getCostBreakdown().then(setData).finally(() => setLoading(false));
  }, []);

  const fmt = useCallback((usd: number) => {
    if (currency === 'inr') {
      const inr = usd * (data?.exchangeRate ?? 85);
      if (inr < 1 && inr > 0) return `${(inr * 100).toFixed(2)} paise`;
      return `₹${inr.toFixed(2)}`;
    }
    if (usd < 0.01 && usd > 0) return `$${usd.toFixed(4)}`;
    return `$${usd.toFixed(2)}`;
  }, [currency, data]);

  if (loading) {
    return (
      <div className={styles['loading-wrapper']}>
        <motion.p
          className={styles['loading-text']}
          animate={{ opacity: [0.4, 1, 0.4] }}
          transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
        >
          Calculating costs…
        </motion.p>
        <motion.div
          className={styles['loading-mascot']}
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: [6, -4, 6], opacity: 1 }}
          transition={{ y: { repeat: Infinity, duration: 2.4, ease: 'easeInOut' }, opacity: { duration: 0.4 } }}
        >
          <MachhaCharacter state="Loading" part="face" size={100} />
        </motion.div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className={styles['empty-center']}>
        <MachhaCharacter state="Error" size={140} />
        <p className={styles['empty-title']}>Failed to load cost data</p>
      </div>
    );
  }

  return (
    <div>
      {/* View toggle: Users / Events */}
      <div className={styles['toggle-group-fit']}>
        {(['users', 'events'] as const).map((v) => (
          <button
            key={v}
            onClick={() => setView(v)}
            className={view === v ? styles['toggle-btn-with-icon-active'] : styles['toggle-btn-with-icon']}
          >
            {v === 'users' ? <Users className={styles['icon-xs']} /> : <Calendar className={styles['icon-xs']} />}
            {v === 'users' ? 'By User' : 'By Event'}
          </button>
        ))}
      </div>

      {/* Total cost summary cards */}
      <motion.div
        className={styles['costs-summary-grid']}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <div className={`${styles['card-clay']} ${styles['costs-summary-card']}`}>
          <div className={styles['costs-summary-card-header']}>
            <div className={`${styles['costs-summary-icon-box']} ${styles['costs-icon-box-indigo']}`}>
              {currency === 'inr' ? <IndianRupee className={[styles['icon-sm'], styles['icon-indigo']].join(' ')} /> : <DollarSign className={[styles['icon-sm'], styles['icon-indigo']].join(' ')} />}
            </div>
            <span className={styles['costs-summary-label']}>Total</span>
          </div>
          <p className={styles['costs-summary-value']}>{fmt(data.totals.total)}</p>
        </div>
        <div className={`${styles['card-clay']} ${styles['costs-summary-card']}`}>
          <div className={styles['costs-summary-card-header']}>
            <div className={`${styles['costs-summary-icon-box']} ${styles['costs-icon-box-orange']}`}>
              <Cpu className={[styles['icon-sm'], styles['icon-orange']].join(' ')} />
            </div>
            <span className={styles['costs-summary-label']}>Lambda</span>
          </div>
          <p className={styles['costs-summary-value']}>{fmt(data.totals.compositor)}</p>
        </div>
        <div className={`${styles['card-clay']} ${styles['costs-summary-card']}`}>
          <div className={styles['costs-summary-card-header']}>
            <div className={`${styles['costs-summary-icon-box']} ${styles['costs-icon-box-teal']}`}>
              <HardDrive className={[styles['icon-sm'], styles['icon-teal']].join(' ')} />
            </div>
            <span className={styles['costs-summary-label']}>Storage</span>
          </div>
          <p className={styles['costs-summary-value']}>{fmt(data.totals.storage)}</p>
        </div>
        <div className={`${styles['card-clay']} ${styles['costs-summary-card']}`}>
          <div className={styles['costs-summary-card-header']}>
            <div className={`${styles['costs-summary-icon-box']} ${styles['costs-icon-box-rose']}`}>
              <Download className={[styles['icon-sm'], styles['icon-rose']].join(' ')} />
            </div>
            <span className={styles['costs-summary-label']}>Transfer</span>
          </div>
          <p className={styles['costs-summary-value']}>{fmt(data.totals.transfer)}</p>
        </div>
      </motion.div>

      {/* Disclaimer */}
      <p className={styles['costs-disclaimer']}>
        Estimates based on ap-south-2 pricing. Lambda: ~1.96s/photo at 1024 MB. Storage: per-month rate. Transfer: S3 outbound.
      </p>

      {/* User breakdown */}
      {view === 'users' && (
        <motion.div className={styles['space-y-2']} variants={clayStaggerContainer} initial="hidden" animate="visible">
          {data.users.map((user) => {
            const isExpanded = expandedUser === user.userId;
            const userEvents = data.events.filter((e) => e.userId === user.userId);
            return (
              <motion.div
                key={user.userId}
                variants={clayStaggerItem}
                className={styles['card-clay']}
              >
                <button
                  className={styles['costs-row-btn']}
                  onClick={() => setExpandedUser(isExpanded ? null : user.userId)}
                >
                  <div className={styles['avatar-circle-sm']}>
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className={styles['avatar-img']} referrerPolicy="no-referrer" />
                    ) : (
                      <div className={styles['avatar-fallback-sm']}>
                        {user.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className={styles['flex-1-min-w-0']}>
                    <p className={styles['devtools-user-name']}>{user.name}</p>
                    <p className={styles['devtools-user-email']}>{user.email}</p>
                  </div>
                  <div className={styles['costs-row-meta']}>
                    <span className={styles['costs-row-meta-item']} title="Lambda">
                      <Cpu className={`${styles['costs-row-meta-icon']} ${styles['icon-orange']}`} />{fmt(user.compositor)}
                    </span>
                    <span className={styles['costs-row-meta-item']} title="Storage">
                      <HardDrive className={`${styles['costs-row-meta-icon']} ${styles['icon-teal']}`} />{fmt(user.storage)}
                    </span>
                    <span className={styles['costs-row-meta-item']} title="Transfer">
                      <Download className={`${styles['costs-row-meta-icon']} ${styles['icon-rose']}`} />{fmt(user.transfer)}
                    </span>
                  </div>
                  <p className={styles['costs-row-total']}>
                    {fmt(user.total)}
                  </p>
                  <motion.div
                    animate={{ rotate: isExpanded ? 180 : 0 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                    className={styles['costs-chevron']}
                  >
                    <ChevronDown className={styles['costs-chevron-icon']} />
                  </motion.div>
                </button>

                <AnimatePresence initial={false}>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2, ease: 'easeInOut' }}
                      className={styles['costs-row-expanded']}
                    >
                      <div className={styles['costs-row-expanded-inner']}>
                        {/* Mobile service breakdown */}
                        <div className={styles['costs-mobile-grid']}>
                          <div className={styles['costs-mobile-cell']}>
                            <Cpu className={`${styles['costs-mobile-cell-icon']} ${styles['icon-orange']}`} />
                            <p className={styles['costs-mobile-cell-label']}>Lambda</p>
                            <p className={styles['costs-mobile-cell-value']}>{fmt(user.compositor)}</p>
                          </div>
                          <div className={styles['costs-mobile-cell']}>
                            <HardDrive className={`${styles['costs-mobile-cell-icon']} ${styles['icon-teal']}`} />
                            <p className={styles['costs-mobile-cell-label']}>Storage</p>
                            <p className={styles['costs-mobile-cell-value']}>{fmt(user.storage)}</p>
                          </div>
                          <div className={styles['costs-mobile-cell']}>
                            <Download className={`${styles['costs-mobile-cell-icon']} ${styles['icon-rose']}`} />
                            <p className={styles['costs-mobile-cell-label']}>Transfer</p>
                            <p className={styles['costs-mobile-cell-value']}>{fmt(user.transfer)}</p>
                          </div>
                        </div>

                        {userEvents.length === 0 ? (
                          <p className={styles['profile-events-empty']}>No events</p>
                        ) : (
                          <div className={styles['space-y-1-5']}>
                            <p className={styles['section-label']}>
                              Events ({userEvents.length})
                            </p>
                            {userEvents.map((ev) => (
                              <div
                                key={ev.eventId}
                                className={styles['costs-sub-row']}
                              >
                                <div className={styles['flex-1-min-w-0']}>
                                  <p className={styles['costs-sub-row-name']}>{ev.eventName}</p>
                                  <p className={styles['costs-sub-row-detail']}>
                                    {ev.albums.length} album{ev.albums.length !== 1 ? 's' : ''}
                                    {ev.albums.length > 0 && (
                                      <> · {ev.albums.reduce((s, a) => s + a.photoCount, 0)} photos</>
                                    )}
                                  </p>
                                </div>
                                <div className={styles['costs-sub-row-actions']}>
                                  <span className={styles['costs-sub-row-inline-meta']}>
                                    <Cpu className={`${styles['costs-sub-row-inline-icon']} ${styles['icon-orange']}`} />{fmt(ev.compositor)}
                                  </span>
                                  <span className={styles['costs-sub-row-inline-meta']}>
                                    <HardDrive className={`${styles['costs-sub-row-inline-icon']} ${styles['icon-teal']}`} />{fmt(ev.storage)}
                                  </span>
                                  <span className={styles['costs-sub-row-cost']}>{fmt(ev.total)}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </motion.div>
      )}

      {/* Event breakdown */}
      {view === 'events' && (
        <motion.div className={styles['space-y-2']} variants={clayStaggerContainer} initial="hidden" animate="visible">
          {data.events.map((ev) => {
            const isExpanded = expandedEvent === ev.eventId;
            return (
              <motion.div
                key={ev.eventId}
                variants={clayStaggerItem}
                className={styles['card-clay']}
              >
                <button
                  className={styles['costs-row-btn']}
                  onClick={() => setExpandedEvent(isExpanded ? null : ev.eventId)}
                >
                  <div className={styles['costs-event-icon-box']}>
                    <Calendar className={styles['costs-event-icon']} />
                  </div>
                  <div className={styles['flex-1-min-w-0']}>
                    <p className={styles['devtools-user-name']}>{ev.eventName}</p>
                    <p className={styles['devtools-user-email']}>
                      {ev.userName} · {ev.albums.length} album{ev.albums.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <div className={styles['costs-row-meta']}>
                    <span className={styles['costs-row-meta-item']}>
                      <Cpu className={`${styles['costs-row-meta-icon']} ${styles['icon-orange']}`} />{fmt(ev.compositor)}
                    </span>
                    <span className={styles['costs-row-meta-item']}>
                      <HardDrive className={`${styles['costs-row-meta-icon']} ${styles['icon-teal']}`} />{fmt(ev.storage)}
                    </span>
                  </div>
                  <p className={styles['costs-row-total']}>
                    {fmt(ev.total)}
                  </p>
                  <motion.div
                    animate={{ rotate: isExpanded ? 180 : 0 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                    className={styles['costs-chevron']}
                  >
                    <ChevronDown className={styles['costs-chevron-icon']} />
                  </motion.div>
                </button>

                <AnimatePresence initial={false}>
                  {isExpanded && ev.albums.length > 0 && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2, ease: 'easeInOut' }}
                      className={styles['costs-row-expanded']}
                    >
                      <div className={`${styles['costs-row-expanded-inner']} ${styles['space-y-1-5']}`}>
                        <p className={styles['section-label']}>
                          Albums
                        </p>
                        {ev.albums.map((album) => (
                          <div
                            key={album.albumId}
                            className={styles['costs-sub-row']}
                          >
                            <div className={styles['flex-1-min-w-0']}>
                              <p className={styles['costs-sub-row-name']}>{album.albumName}</p>
                              <p className={styles['costs-sub-row-detail']}>
                                {album.photoCount} photos · {album.configVersion} version{album.configVersion !== 1 ? 's' : ''} processed
                              </p>
                            </div>
                            <div className={styles['costs-sub-row-actions']}>
                              <span className={styles['costs-sub-row-cost']}>
                                <Cpu className={`${styles['costs-sub-row-inline-icon']} ${styles['icon-orange']}`} />
                                {fmt(album.compositorCost)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}

          {data.events.length === 0 && (
            <div className={styles['costs-no-events']}>
              <MachhaCharacter state="Empty" size={140} />
              <p className={styles['costs-no-events-title']}>No events yet</p>
              <p className={styles['costs-no-events-subtitle']}>Cost data will appear when users create events and albums.</p>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
};

// ─── CostsTab (main wrapper) ────────────────────────────────────────────────

const CostsTab: React.FC = () => {
  const [mode, setMode] = useState<CostMode>('billing');
  const [currency, setCurrency] = useState<Currency>('inr');

  return (
    <div>
      {/* Top bar: Mode toggle + Currency toggle */}
      <div className={styles['costs-top-bar']}>
        {/* Mode toggle: AWS Billing / Estimated */}
        <div className={styles['toggle-group']}>
          {([
            { key: 'billing' as const, icon: CloudCog, label: 'AWS Billing' },
            { key: 'estimated' as const, icon: TrendingUp, label: 'Estimated' },
          ]).map(({ key, icon: Icon, label }) => (
            <motion.button
              key={key}
              onClick={() => setMode(key)}
              className={styles['toggle-btn-with-icon']}
              whileTap={{ scale: 0.95 }}
            >
              <Icon className={styles['icon-xs']} />
              {label}
              {mode === key && (
                <motion.div
                  layoutId="costs-mode-indicator"
                  className={styles['toggle-indicator']}
                  transition={{ type: 'spring', stiffness: 500, damping: 40 }}
                />
              )}
            </motion.button>
          ))}
        </div>

        {/* Currency toggle */}
        <div className={styles['toggle-group']}>
          {(['inr', 'usd'] as const).map((c) => (
            <motion.button
              key={c}
              onClick={() => setCurrency(c)}
              className={styles['toggle-btn-with-icon']}
              whileTap={{ scale: 0.95 }}
            >
              {c === 'inr' ? <IndianRupee className={styles['icon-xs']} /> : <DollarSign className={styles['icon-xs']} />}
              {c === 'inr' ? 'INR' : 'USD'}
              {currency === c && (
                <motion.div
                  layoutId="costs-currency-indicator"
                  className={styles['toggle-indicator']}
                  transition={{ type: 'spring', stiffness: 500, damping: 40 }}
                />
              )}
            </motion.button>
          ))}
        </div>
      </div>

      {mode === 'billing' ? <AwsBillingView currency={currency} /> : <EstimatedCostsView currency={currency} />}
    </div>
  );
};

// ─── QuotasTab ───────────────────────────────────────────────────────────────

const QUOTA_TYPE_LABELS: Record<QuotaType, string> = {
  events: 'Events',
  photosPerEvent: 'Photos/Event',
  albumsPerEvent: 'Albums/Event',
  ftpAccountsPerEvent: 'FTP/Event',
  liveAlbumsPerEvent: 'Live Albums/Event',
};

const QuotasTab: React.FC<{ onLoad: (count: number) => void }> = ({ onLoad }) => {
  const [requests, setRequests] = useState<QuotaRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [approveId, setApproveId] = useState<string | null>(null);
  const [newLimit, setNewLimit] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchPage = useCallback(async (p: number) => {
    setLoading(true);
    try {
      const data = await adminApi.listQuotaRequests(p);
      setRequests(data.requests);
      setTotalPages(data.pagination.pages);
      setPage(p);
      onLoad(data.pendingCount);
    } catch {
      toast.error('Failed to load quota requests');
    } finally {
      setLoading(false);
    }
  }, [onLoad]);

  useEffect(() => { fetchPage(1); }, [fetchPage]);

  const handleAction = async (id: string, status: 'approved' | 'rejected') => {
    setActionLoading(true);
    try {
      const updated = await adminApi.updateQuotaRequest(id, {
        status,
        ...(status === 'approved' && newLimit ? { newLimit: parseInt(newLimit, 10) } : {}),
      });
      setRequests((prev) => prev.map((r) => (r._id === id ? updated : r)));
      setApproveId(null);
      setNewLimit('');
      toast.success(`Request ${status}`);
      // Refresh pending count
      adminApi.listQuotaRequests(1).then((d) => onLoad(d.pendingCount)).catch(() => { });
    } catch {
      toast.error('Failed to update request');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div>
      {loading ? (
        <div className={styles['loading-wrapper']}>
          <motion.p
            className={styles['loading-text']}
            animate={{ opacity: [0.4, 1, 0.4] }}
            transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
          >
            Loading quota requests…
          </motion.p>
        </div>
      ) : requests.length === 0 ? (
        <div className={styles['empty-center']}>
          <MachhaCharacter state="Empty" size={140} />
          <p className={styles['empty-title']}>No quota requests</p>
          <p className={styles['empty-subtitle']}>Requests from users who hit their limits will appear here.</p>
        </div>
      ) : (
        <motion.div className={styles['space-y-2']} variants={clayStaggerContainer} initial="hidden" animate="visible">
          {requests.map((req) => {
            const user = typeof req.userId === 'object' ? req.userId : null;
            const isPending = req.status === 'pending';
            return (
              <motion.div
                key={req._id}
                variants={clayStaggerItem}
                className={`${styles['card-clay']} ${styles['quota-card']}`}
              >
                <div className={styles['quota-row']}>
                  {/* Avatar */}
                  {user?.picture ? (
                    <img src={user.picture} alt={user.name} referrerPolicy="no-referrer" className={styles['quota-avatar']} />
                  ) : (
                    <div className={`${styles['avatar-circle-md']} ${styles['avatar-fallback-neutral']}`}>
                      {user?.name?.charAt(0).toUpperCase() ?? '?'}
                    </div>
                  )}

                  <div className={styles['flex-1-min-w-0']}>
                    <div className={styles['quota-name-row']}>
                      <p className={styles['quota-name']}>
                        {user?.name ?? 'Unknown'}
                      </p>
                      <Badge variant={isPending ? 'yellow' : req.status === 'approved' ? 'green' : 'red'}>
                        {req.status}
                      </Badge>
                      <Badge variant="blue">{QUOTA_TYPE_LABELS[req.quotaType]}</Badge>
                    </div>
                    <p className={styles['quota-email']}>
                      {user?.email ?? ''} — Current limit: {req.currentLimit}
                    </p>
                    {req.reason && (
                      <p className={styles['quota-reason']}>
                        "{req.reason}"
                      </p>
                    )}
                  </div>

                  <div className={styles['flex-shrink-0']}>
                    <p className={styles['quota-date']}>
                      {formatDate(req.createdAt)}
                    </p>
                  </div>
                </div>

                {/* Actions for pending requests */}
                {isPending && (
                  <div className={styles['quota-actions']}>
                    {approveId === req._id ? (
                      <div className={styles['quota-approve-input-row']}>
                        <input
                          type="number"
                          value={newLimit}
                          onChange={(e) => setNewLimit(e.target.value)}
                          placeholder="New limit"
                          className={styles['quota-limit-input']}
                        />
                        <Button
                          onClick={() => handleAction(req._id, 'approved')}
                          loading={actionLoading}
                          disabled={!newLimit}
                        >
                          Confirm
                        </Button>
                        <Button variant="ghost" onClick={() => { setApproveId(null); setNewLimit(''); }}>
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <div className={styles['quota-btn-row']}>
                        <Button onClick={() => { setApproveId(req._id); setNewLimit(String(req.currentLimit * 2)); }}>
                          Approve
                        </Button>
                        <Button variant="danger" onClick={() => handleAction(req._id, 'rejected')} loading={actionLoading}>
                          Reject
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
            );
          })}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className={styles['pagination-row']}>
              <button
                onClick={() => fetchPage(page - 1)}
                disabled={page <= 1}
                className={styles['pagination-btn']}
              >
                Prev
              </button>
              <span className={styles['pagination-info']}>{page} / {totalPages}</span>
              <button
                onClick={() => fetchPage(page + 1)}
                disabled={page >= totalPages}
                className={styles['pagination-btn']}
              >
                Next
              </button>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
};

// ─── SecurityTab ─────────────────────────────────────────────────────────────

const SecurityTab: React.FC = () => {
  const [blockedIPs, setBlockedIPs] = useState<AdminBlockedIP[]>([]);
  const [loading, setLoading] = useState(true);
  const [unblocking, setUnblocking] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.listBlockedIPs();
      setBlockedIPs(data.blockedIPs);
    } catch {
      toast.error('Failed to load blocked IPs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleUnblock = async (id: string) => {
    setUnblocking(id);
    try {
      await adminApi.unblockIP(id);
      setBlockedIPs((prev) => prev.map((b) => b._id === id ? { ...b, unblockedAt: new Date().toISOString() } : b));
      toast.success('IP unblocked');
    } catch {
      toast.error('Failed to unblock IP');
    } finally {
      setUnblocking(null);
    }
  };

  const activeBlocks = blockedIPs.filter((b) => !b.unblockedAt);
  const pastBlocks = blockedIPs.filter((b) => b.unblockedAt);

  if (loading) {
    return (
      <div className={styles['loading-wrapper']}>
        <motion.p
          className={styles['loading-text']}
          animate={{ opacity: [0.4, 1, 0.4] }}
          transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
        >
          Loading security data…
        </motion.p>
      </div>
    );
  }

  return (
    <div className={styles['space-y-6']}>
      {/* Active blocks */}
      <section>
        <h3 className={styles['security-section-heading']}>
          Active Blocks ({activeBlocks.length})
        </h3>
        {activeBlocks.length === 0 ? (
          <motion.div
            className={`${styles['card-clay-lg']} ${styles['security-clear-card']}`}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <motion.div
              className={styles['security-clear-mascot']}
              animate={{ y: [0, -5, 0], rotate: [8, 12, 8] }}
              transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}
            >
              <MachhaCharacter state="Success" part="face" size={68} />
            </motion.div>
            <p className={styles['security-clear-title']}>No blocked IPs</p>
            <p className={styles['security-clear-subtitle']}>All clear — no brute force activity detected</p>
          </motion.div>
        ) : (
          <motion.div className={styles['space-y-2']} variants={clayStaggerContainer} initial="hidden" animate="visible">
            {activeBlocks.map((block) => (
              <motion.div
                key={block._id}
                variants={clayStaggerItem}
                className={`${styles['card-clay']} ${styles['security-block-card']}`}
              >
                <div className={styles['flex-1-min-w-0']}>
                  <div className={styles['security-block-ip-row']}>
                    <span className={styles['security-block-ip']}>{block.ip}</span>
                    <Badge variant="red">Blocked</Badge>
                  </div>
                  <p className={styles['security-block-reason']}>{block.reason}</p>
                  <p className={styles['security-block-date']}>{formatDate(block.blockedAt)}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  loading={unblocking === block._id}
                  onClick={() => handleUnblock(block._id)}
                >
                  Unblock
                </Button>
              </motion.div>
            ))}
          </motion.div>
        )}
      </section>

      {/* History */}
      {pastBlocks.length > 0 && (
        <section>
          <h3 className={styles['security-section-heading']}>
            History ({pastBlocks.length})
          </h3>
          <div className={styles['space-y-2']}>
            {pastBlocks.map((block) => (
              <div
                key={block._id}
                className={styles['security-history-card']}
              >
                <div className={styles['security-history-ip-row']}>
                  <span className={styles['security-history-ip']}>{block.ip}</span>
                  <Badge variant="blue">Unblocked</Badge>
                </div>
                <p className={styles['security-history-reason']}>{block.reason}</p>
                <p className={styles['security-history-dates']}>
                  Blocked {formatDate(block.blockedAt)} · Unblocked {block.unblockedAt ? formatDate(block.unblockedAt) : ''}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

// ─── TwilioTab ───────────────────────────────────────────────────────────────

const fmtUsd = (n: number) => `$${n.toFixed(2)}`;

const defaultTwilioRange = () => {
  const now = new Date();
  const end = now.toISOString().slice(0, 10);
  const start = new Date(now.getTime() - 30 * 86400000).toISOString().slice(0, 10);
  return { start, end };
};

const TwilioMessageRowCard: React.FC<{ row: TwilioMessageRow }> = ({ row }) => {
  const [unmasked, setUnmasked] = useState<{ to: string; from: string } | null>(null);
  const [loading, setLoading] = useState(false);

  const toggle = async () => {
    if (unmasked) { setUnmasked(null); return; }
    setLoading(true);
    try {
      const data = await twilioApi.unmaskMessage(row.sid);
      setUnmasked({ to: data.to, from: data.from });
    } catch {
      toast.error('Failed to unmask');
    } finally {
      setLoading(false);
    }
  };

  const isError = row.errorCode !== null;
  const channelLabel = row.channel === 'whatsapp' ? 'WhatsApp' : row.channel === 'sms' ? 'SMS' : 'Other';

  return (
    <div className={`${styles['card-clay']} ${styles['twilio-message-row']}`}>
      <div className={styles['twilio-message-header']}>
        <span className={`${styles['twilio-channel-badge']} ${styles[`twilio-channel-${row.channel}`]}`}>
          {channelLabel}
        </span>
        <span className={styles['twilio-message-status']} data-error={isError || undefined}>
          {row.status}
          {isError && row.errorCode ? ` · ${row.errorCode}` : ''}
        </span>
        <span className={styles['twilio-message-date']}>
          {row.dateSent ? new Date(row.dateSent).toLocaleString() : '—'}
        </span>
      </div>
      <div className={styles['twilio-message-addr']}>
        <span className={styles['twilio-message-addr-label']}>To</span>
        <code className={styles['twilio-message-addr-value']}>{unmasked ? unmasked.to : row.to}</code>
        <button
          className={styles['twilio-unmask-btn']}
          onClick={toggle}
          disabled={loading}
          title={unmasked ? 'Mask' : 'Unmask'}
          type="button"
        >
          {unmasked ? <EyeOff size={14} /> : <Eye size={14} />}
        </button>
        {unmasked && <span className={styles['twilio-unmasked-indicator']}>unmasked</span>}
      </div>
      {row.body && (
        <p className={styles['twilio-message-body']}>{row.body}</p>
      )}
      <div className={styles['twilio-message-footer']}>
        <span className={styles['twilio-message-from']}>from {unmasked ? unmasked.from : row.from}</span>
        {row.price !== null && (
          <span className={styles['twilio-message-price']}>{fmtUsd(Math.abs(row.price))}</span>
        )}
      </div>
      {isError && row.errorMessage && (
        <p className={styles['twilio-message-error']}>{row.errorMessage}</p>
      )}
    </div>
  );
};

const TwilioTab: React.FC = () => {
  const initial = defaultTwilioRange();
  const [startDate, setStartDate] = useState(initial.start);
  const [endDate, setEndDate] = useState(initial.end);
  const [appliedRange, setAppliedRange] = useState(initial);
  const [stats, setStats] = useState<TwilioStatsResponse | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState<string | null>(null);

  const [channel, setChannel] = useState<TwilioChannelFilter>('all');
  const [messages, setMessages] = useState<TwilioMessageRow[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(true);
  const [messagesError, setMessagesError] = useState<string | null>(null);
  const [nextPageToken, setNextPageToken] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const loadStats = useCallback(() => {
    setStatsLoading(true);
    setStatsError(null);
    twilioApi.getStats({ startDate: appliedRange.start, endDate: appliedRange.end })
      .then(setStats)
      .catch((err) => setStatsError(err?.response?.data?.error ?? 'Failed to load Twilio stats'))
      .finally(() => setStatsLoading(false));
  }, [appliedRange]);

  const loadMessagesPage = useCallback((pageToken: string | null, append: boolean) => {
    if (append) setLoadingMore(true); else setMessagesLoading(true);
    setMessagesError(null);
    twilioApi.getMessages({
      startDate: appliedRange.start,
      endDate: appliedRange.end,
      channel,
      limit: 50,
      pageToken,
    })
      .then((res) => {
        setMessages((prev) => append ? [...prev, ...res.messages] : res.messages);
        setNextPageToken(res.nextPageToken);
      })
      .catch((err) => setMessagesError(err?.response?.data?.error ?? 'Failed to load messages'))
      .finally(() => { setMessagesLoading(false); setLoadingMore(false); });
  }, [appliedRange, channel]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadMessagesPage(null, false); }, [loadMessagesPage]);

  const applyRange = () => {
    if (!startDate || !endDate) return;
    setAppliedRange({ start: startDate, end: endDate });
  };

  return (
    <div>
      {/* Date range controls */}
      <div className={`${styles['card-clay']} ${styles['twilio-controls']}`}>
        <div className={styles['twilio-control-group']}>
          <label className={styles['costs-date-label']}>Start</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className={styles['costs-date-input']}
          />
        </div>
        <div className={styles['twilio-control-group']}>
          <label className={styles['costs-date-label']}>End</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className={styles['costs-date-input']}
          />
        </div>
        <button
          onClick={applyRange}
          disabled={!startDate || !endDate}
          className={styles['costs-apply-btn']}
        >
          Apply
        </button>
      </div>

      {/* Stats summary */}
      {statsLoading ? (
        <div className={styles['loading-wrapper']}>
          <motion.p className={styles['loading-text']} animate={{ opacity: [0.4, 1, 0.4] }} transition={{ repeat: Infinity, duration: 2 }}>
            Fetching Twilio usage…
          </motion.p>
          <motion.div className={styles['loading-mascot']} initial={{ y: 40, opacity: 0 }} animate={{ y: [6, -4, 6], opacity: 1 }} transition={{ y: { repeat: Infinity, duration: 2.4 }, opacity: { duration: 0.4 } }}>
            <MachhaCharacter state="Loading" part="face" size={100} />
          </motion.div>
        </div>
      ) : statsError ? (
        <div className={styles['costs-error-center']}>
          <MachhaCharacter state="Error" size={140} />
          <p className={styles['costs-error-title']}>Failed to load Twilio stats</p>
          <p className={styles['costs-error-msg']}>{statsError}</p>
          <button onClick={loadStats} className={styles['costs-retry-btn']}>Retry</button>
        </div>
      ) : stats && (
        <>
          <div className={styles['twilio-stats-grid']}>
            <div className={`${styles['card-clay']} ${styles['stat-card']}`}>
              <div className={styles['stat-icon-box-orange']}>
                <Wallet className={`${styles['stat-icon']} ${styles['icon-orange']}`} />
              </div>
              <div>
                <p className={styles['stat-value']}>
                  {stats.balance ? `${fmtUsd(stats.balance.amount)}` : '—'}
                </p>
                <p className={styles['stat-label']}>Balance ({stats.balance?.currency ?? 'USD'})</p>
              </div>
            </div>
            <div className={`${styles['card-clay']} ${styles['stat-card']}`}>
              <div className={styles['stat-icon-box-orange']}>
                <DollarSign className={`${styles['stat-icon']} ${styles['icon-orange']}`} />
              </div>
              <div>
                <p className={styles['stat-value']}>{fmtUsd(stats.costs.total)}</p>
                <p className={styles['stat-label']}>Total spend · {appliedRange.start} → {appliedRange.end}</p>
              </div>
            </div>
            <div className={`${styles['card-clay']} ${styles['stat-card']}`}>
              <div className={styles['stat-icon-box-orange']}>
                <MessageCircle className={`${styles['stat-icon']} ${styles['icon-orange']}`} />
              </div>
              <div>
                <p className={styles['stat-value']}>{stats.counts.sms + stats.counts.whatsapp}</p>
                <p className={styles['stat-label']}>Messages ({stats.counts.sms} SMS · {stats.counts.whatsapp} WA)</p>
              </div>
            </div>
            <div className={`${styles['card-clay']} ${styles['stat-card']}`}>
              <div className={styles['stat-icon-box-orange']}>
                <Shield className={`${styles['stat-icon']} ${styles['icon-orange']}`} />
              </div>
              <div>
                <p className={styles['stat-value']}>{stats.counts.verify}</p>
                <p className={styles['stat-label']}>OTP verifications</p>
              </div>
            </div>
          </div>

          <div className={styles['twilio-breakdown']}>
            <p className={styles['section-label']}>Cost breakdown</p>
            <div className={styles['twilio-breakdown-chips']}>
              <div className={styles['twilio-chip']}>
                <span className={styles['twilio-chip-label']}>SMS</span>
                <span className={styles['twilio-chip-value']}>{fmtUsd(stats.costs.sms)}</span>
              </div>
              <div className={styles['twilio-chip']}>
                <span className={styles['twilio-chip-label']}>WhatsApp</span>
                <span className={styles['twilio-chip-value']}>{fmtUsd(stats.costs.whatsapp)}</span>
              </div>
              <div className={styles['twilio-chip']}>
                <span className={styles['twilio-chip-label']}>Verify (OTP)</span>
                <span className={styles['twilio-chip-value']}>{fmtUsd(stats.costs.verify)}</span>
              </div>
              {stats.costs.other > 0 && (
                <div className={styles['twilio-chip']}>
                  <span className={styles['twilio-chip-label']}>Other</span>
                  <span className={styles['twilio-chip-value']}>{fmtUsd(stats.costs.other)}</span>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Channel filter + message log */}
      <div className={styles['twilio-log-header']}>
        <p className={styles['section-label']}>Message log</p>
        <div className={styles['twilio-filter-row']}>
          {(['all', 'sms', 'whatsapp'] as const).map((c) => (
            <button
              key={c}
              onClick={() => setChannel(c)}
              className={channel === c ? styles['costs-toggle-btn-wide-active'] : styles['costs-toggle-btn-wide']}
              type="button"
            >
              {c === 'all' ? 'All' : c === 'sms' ? 'SMS' : 'WhatsApp'}
            </button>
          ))}
        </div>
      </div>

      {messagesLoading ? (
        <div className={styles['loading-wrapper']}>
          <motion.p className={styles['loading-text']} animate={{ opacity: [0.4, 1, 0.4] }} transition={{ repeat: Infinity, duration: 2 }}>
            Fetching messages…
          </motion.p>
        </div>
      ) : messagesError ? (
        <div className={styles['costs-error-center']}>
          <p className={styles['costs-error-msg']}>{messagesError}</p>
          <button onClick={() => loadMessagesPage(null, false)} className={styles['costs-retry-btn']}>Retry</button>
        </div>
      ) : messages.length === 0 ? (
        <div className={styles['empty-center']}>
          <MachhaCharacter state="Empty" part="face" size={90} />
          <p className={styles['empty-title']}>No messages in this range</p>
        </div>
      ) : (
        <>
          <div className={styles['twilio-message-list']}>
            {messages.map((m) => <TwilioMessageRowCard key={m.sid} row={m} />)}
          </div>
          {nextPageToken && (
            <div className={styles['twilio-load-more']}>
              <button
                onClick={() => loadMessagesPage(nextPageToken, true)}
                disabled={loadingMore}
                className={styles['costs-apply-btn']}
                type="button"
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ─── BrandingTab ─────────────────────────────────────────────────────────────

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

// ── Reprocess albums modal ─────────────────────────────────────────────────

// Strategy thresholds must match the backend (adminController.ts)
const REPROCESS_PARALLEL_ALBUM_LIMIT = 10;
const REPROCESS_PARALLEL_PHOTO_LIMIT = 500;

const STATUS_LABEL: Record<AdminAffectedAlbum['processingStatus'], string> = {
  idle: 'idle',
  processing: 'processing',
  ready: 'ready',
  failed: 'failed',
};

const STATUS_CLASS: Record<AdminAffectedAlbum['processingStatus'], string> = {
  idle: 'reprocess-status-idle',
  processing: 'reprocess-status-processing',
  ready: 'reprocess-status-ready',
  failed: 'reprocess-status-failed',
};

const ReprocessAlbumsModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onReprocessed: () => void;
}> = ({ isOpen, onClose, onReprocessed }) => {
  const [albums, setAlbums] = useState<AdminAffectedAlbum[]>([]);
  const [diagnostics, setDiagnostics] = useState<AdminAffectedAlbumsDiagnostics | null>(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [includeAll, setIncludeAll] = useState(false);

  const loadAlbums = useCallback((includeAllArg: boolean) => {
    setLoading(true);
    adminApi.listAffectedAlbums(includeAllArg)
      .then((data) => {
        setAlbums(data.albums);
        setDiagnostics(data.diagnostics);
      })
      .catch(() => toast.error('Failed to load affected albums'))
      .finally(() => setLoading(false));
  }, []);

  // Reset transient state on modal open
  useEffect(() => {
    if (!isOpen) return;
    setSelected(new Set());
    setSearch('');
    setIncludeAll(false);
  }, [isOpen]);

  // Fetch whenever the modal opens or the include-all toggle changes
  useEffect(() => {
    if (!isOpen) return;
    setSelected(new Set());
    loadAlbums(includeAll);
  }, [isOpen, includeAll, loadAlbums]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return albums;
    return albums.filter((a) =>
      a.name.toLowerCase().includes(q)
      || (a.eventName ?? '').toLowerCase().includes(q)
      || (a.creatorName ?? '').toLowerCase().includes(q)
      || (a.creatorEmail ?? '').toLowerCase().includes(q),
    );
  }, [albums, search]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allVisibleSelected = filtered.length > 0 && filtered.every((a) => selected.has(a._id));
  const toggleAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        filtered.forEach((a) => next.delete(a._id));
      } else {
        filtered.forEach((a) => next.add(a._id));
      }
      return next;
    });
  };

  const selectedAlbums = albums.filter((a) => selected.has(a._id));
  const selectedPhotoCount = selectedAlbums.reduce((s, a) => s + a.photoCount, 0);

  // Predict strategy (must match backend thresholds)
  const predictedStrategy: 'parallel' | 'queued' =
    selectedAlbums.length <= REPROCESS_PARALLEL_ALBUM_LIMIT
    && selectedPhotoCount <= REPROCESS_PARALLEL_PHOTO_LIMIT
      ? 'parallel'
      : 'queued';

  const handleReprocess = async () => {
    if (selectedAlbums.length === 0) return;
    setSubmitting(true);
    try {
      const result = await adminApi.reprocessAffectedAlbums(selectedAlbums.map((a) => a._id));
      const strategyLabel = result.strategy === 'parallel'
        ? 'fire-and-forget'
        : `queued (${result.concurrency} at a time)`;
      toast.success(
        `Queued ${result.queued} album${result.queued === 1 ? '' : 's'} · ${result.totalPhotos} photos · ${strategyLabel}`,
        { duration: 5000 },
      );
      onReprocessed();
      onClose();
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error || 'Failed to start reprocessing');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Reprocess Affected Albums" size="lg">
      <div className={styles['reprocess-body']}>
        <div className={styles['reprocess-summary-row']}>
          <span className={styles['reprocess-summary-text']}>
            {loading ? 'Loading…' : (
              <>
                Found <span className={styles['reprocess-summary-strong']}>{albums.length}</span>{' '}
                album{albums.length === 1 ? '' : 's'} using the default watermark.{' '}
                Select which ones to re-composite with the current logo + config.
              </>
            )}
          </span>
        </div>

        <div className={styles['reprocess-toolbar']}>
          <input
            type="text"
            placeholder="Search by album, event, or creator…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={styles['reprocess-search']}
          />
          <label className={styles['reprocess-mode-toggle']} title="Show every album in the database, including ones that don't yet have the default overlay. The reprocess action will inject it.">
            <input
              type="checkbox"
              checked={includeAll}
              onChange={(e) => setIncludeAll(e.target.checked)}
            />
            Include albums without it
          </label>
          <button
            type="button"
            onClick={toggleAllVisible}
            className={styles['reprocess-select-all']}
            disabled={filtered.length === 0}
          >
            {allVisibleSelected ? 'Clear visible' : 'Select all visible'}
          </button>
        </div>

        <div className={styles['reprocess-list']}>
          {loading ? (
            <div className={styles['reprocess-empty']}>Loading albums…</div>
          ) : filtered.length === 0 ? (
            albums.length === 0 && diagnostics ? (
              <div className={styles['reprocess-empty-diag']}>
                <span className={styles['reprocess-empty-diag-title']}>
                  No albums reference the default overlay yet.
                </span>
                <span>
                  By design, the default watermark is auto-injected only for{' '}
                  <strong>non-owner</strong> users on album creation. Owner-created
                  albums never get it. Diagnostics from your database:
                </span>
                <ul>
                  <li>
                    Default overlay records (<code>{diagnostics.defaultOverlayS3Path}</code>):{' '}
                    <strong>{diagnostics.defaultOverlayRecordCount}</strong>
                  </li>
                  <li>
                    Albums referencing the default overlay:{' '}
                    <strong>{diagnostics.albumsWithDefaultOverlayCount}</strong>
                  </li>
                  <li>
                    Albums with any overlay:{' '}
                    <strong>{diagnostics.albumsWithAnyOverlayCount}</strong>
                  </li>
                  <li>
                    Total albums in DB: <strong>{diagnostics.totalAlbumsCount}</strong>
                  </li>
                </ul>
                <span>
                  To apply the default branding to albums that don't have it (e.g.
                  owner-created ones), tick <strong>Include albums without it</strong>{' '}
                  above. The reprocess action will add the default overlay before
                  recompositing.
                </span>
              </div>
            ) : (
              <div className={styles['reprocess-empty']}>
                {albums.length === 0
                  ? 'No albums found.'
                  : 'No albums match your search.'}
              </div>
            )
          ) : (
            filtered.map((album) => {
              const isChecked = selected.has(album._id);
              return (
                <div
                  key={album._id}
                  onClick={() => toggle(album._id)}
                  className={isChecked ? styles['reprocess-row-checked'] : styles['reprocess-row']}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); toggle(album._id); } }}
                >
                  <div className={isChecked ? styles['reprocess-checkbox-checked'] : styles['reprocess-checkbox']}>
                    {isChecked && <Check className={styles['reprocess-checkbox-icon']} />}
                  </div>
                  <div className={styles['reprocess-row-main']}>
                    <span className={styles['reprocess-row-title']}>
                      {album.name}
                      {album.isLive && (
                        <span style={{ marginLeft: 6 }}>
                          <Badge variant="red">LIVE</Badge>
                        </span>
                      )}
                    </span>
                    <span className={styles['reprocess-row-meta']}>
                      {album.eventName ?? 'No event'}
                      <span className={styles['reprocess-row-meta-sep']}>·</span>
                      {album.creatorName ?? album.creatorEmail ?? 'Unknown'}
                    </span>
                  </div>
                  <div className={styles['reprocess-row-right']}>
                    <span className={styles['reprocess-row-photos']}>
                      {album.photoCount} photo{album.photoCount === 1 ? '' : 's'}
                    </span>
                    <span className={`${styles['reprocess-row-status']} ${styles[STATUS_CLASS[album.processingStatus]]}`}>
                      {STATUS_LABEL[album.processingStatus]}
                    </span>
                    {!album.hasDefaultOverlay && (
                      <span className={styles['reprocess-no-default-chip']}>
                        will inject
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className={styles['reprocess-footer']}>
          <div className={styles['reprocess-footer-stats']}>
            {selectedAlbums.length > 0 ? (
              <>
                <strong>{selectedAlbums.length}</strong> selected · <strong>{selectedPhotoCount}</strong> photos
                <span className={styles['reprocess-strategy-hint']}>
                  Strategy: {predictedStrategy === 'parallel'
                    ? 'fire-and-forget (all at once)'
                    : 'queued (5 concurrent at a time)'}
                </span>
              </>
            ) : (
              <>No albums selected</>
            )}
          </div>
          <div className={styles['reprocess-footer-actions']}>
            <Button variant="ghost" size="md" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={handleReprocess}
              loading={submitting}
              disabled={selectedAlbums.length === 0 || submitting}
            >
              <PlayCircle className={styles['tab-icon']} />
              Reprocess {selectedAlbums.length > 0 ? `(${selectedAlbums.length})` : ''}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

const ANCHOR_GRID: { row: number; col: number; anchor: OverlayAnchor }[] = [
  { row: 0, col: 0, anchor: 'top-left' },
  { row: 0, col: 1, anchor: 'top-center' },
  { row: 0, col: 2, anchor: 'top-right' },
  { row: 1, col: 0, anchor: 'middle-left' },
  { row: 1, col: 1, anchor: 'middle-center' },
  { row: 1, col: 2, anchor: 'middle-right' },
  { row: 2, col: 0, anchor: 'bottom-left' },
  { row: 2, col: 1, anchor: 'bottom-center' },
  { row: 2, col: 2, anchor: 'bottom-right' },
];

const SIZE_OPTIONS: OverlaySize[] = ['xs', 'sm', 'md', 'lg', 'xl'];

const BrandingTab: React.FC = () => {
  const [branding, setBranding] = useState<AdminDefaultBranding | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [configDraft, setConfigDraft] = useState<AdminDefaultBrandingConfig | null>(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [reprocessOpen, setReprocessOpen] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.getDefaultBranding();
      setBranding(data);
      setConfigDraft(data.config);
    } catch {
      toast.error('Failed to load default branding');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'image/png') {
      toast.error('Only PNG files are allowed');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File must be under 10 MB');
      return;
    }
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    try {
      const updated = await adminApi.uploadDefaultBranding(selectedFile);
      setBranding(updated);
      setConfigDraft(updated.config);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      toast.success('Default branding logo updated');
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error || 'Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const isConfigDirty = useMemo(() => {
    if (!branding || !configDraft) return false;
    return (
      configDraft.anchor !== branding.config.anchor
      || configDraft.size !== branding.config.size
      || configDraft.opacity !== branding.config.opacity
    );
  }, [branding, configDraft]);

  const handleSaveConfig = async () => {
    if (!configDraft || !isConfigDirty) return;
    setSavingConfig(true);
    try {
      const updated = await adminApi.updateDefaultBrandingConfig(configDraft);
      setBranding(updated);
      setConfigDraft(updated.config);
      const matched = updated.propagation?.modifiedAlbums ?? 0;
      toast.success(
        matched > 0
          ? `Config saved — ${matched} album${matched === 1 ? '' : 's'} updated`
          : 'Config saved',
      );
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error || 'Failed to save config');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleResetConfig = () => {
    if (branding) setConfigDraft(branding.config);
  };

  if (loading) {
    return (
      <div className={styles['loading-wrapper']}>
        <motion.p
          className={styles['loading-text']}
          animate={{ opacity: [0.4, 1, 0.4] }}
          transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
        >
          Loading branding settings…
        </motion.p>
      </div>
    );
  }

  return (
    <div className={styles['space-y-6']}>
      <div className={styles['branding-info-box']}>
        <Info className={styles['branding-info-icon']} />
        <span>
          This PNG is auto-applied as a watermark overlay to every album and live album
          created by beta-approved users. Upload your brand logo here to make it live for
          all current and future albums.
        </span>
      </div>

      <section className={`${styles['card-clay-lg']} ${styles['branding-card']}`}>
        <div className={styles['branding-header-row']}>
          <div>
            <h3 className={styles['branding-title']}>Default Branding Logo</h3>
            <p className={styles['branding-subtitle']}>
              Currently used as the automatic watermark on all beta-user albums.
              Recommended: transparent PNG, wide aspect ratio, white text.
            </p>
          </div>
        </div>

        <div className={styles['branding-preview-box']}>
          {branding?.exists && branding.url ? (
            <img
              src={branding.url}
              alt="Default branding logo"
              className={styles['branding-preview-img']}
            />
          ) : (
            <div className={styles['branding-preview-empty']}>
              <Image className={styles['tab-icon']} />
              <span>No logo configured yet — upload a PNG below</span>
            </div>
          )}
        </div>

        <div className={styles['branding-meta-row']}>
          <div>
            <span className={styles['branding-meta-label']}>S3 Path</span>
            <span className={styles['branding-path-value']}>{branding?.storagePath ?? '—'}</span>
          </div>
          {branding?.exists && (
            <>
              <div>
                <span className={styles['branding-meta-label']}>Size</span>
                <span className={styles['branding-meta-value']}>
                  {branding.size ? formatBytes(branding.size) : '—'}
                </span>
              </div>
              <div>
                <span className={styles['branding-meta-label']}>Last Updated</span>
                <span className={styles['branding-meta-value']}>
                  {branding.lastModified ? formatDate(branding.lastModified) : '—'}
                </span>
              </div>
            </>
          )}
        </div>

        <div className={styles['branding-upload-row']}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png"
            onChange={handleFileChange}
            className={styles['branding-file-input']}
            id="branding-file-input"
          />
          <Button
            variant="secondary"
            size="md"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            <Upload className={styles['tab-icon']} />
            Choose PNG
          </Button>
          {selectedFile && (
            <span className={styles['branding-file-name']} title={selectedFile.name}>
              {selectedFile.name}
            </span>
          )}
          <Button
            variant="primary"
            size="md"
            onClick={handleUpload}
            loading={uploading}
            disabled={!selectedFile || uploading}
          >
            {branding?.exists ? 'Replace Logo' : 'Upload Logo'}
          </Button>
        </div>

        {/* Config: anchor / size / opacity */}
        {configDraft && (
          <div className={styles['branding-config-grid']}>
            <div className={styles['branding-config-group']}>
              <span className={styles['branding-config-label']}>Position</span>
              <div className={styles['branding-anchor-grid']}>
                {ANCHOR_GRID.map(({ anchor }) => {
                  const active = configDraft.anchor === anchor;
                  return (
                    <button
                      key={anchor}
                      type="button"
                      onClick={() => setConfigDraft({ ...configDraft, anchor })}
                      className={active ? styles['branding-anchor-cell-active'] : styles['branding-anchor-cell']}
                      title={anchor}
                      aria-label={`Anchor ${anchor}`}
                      aria-pressed={active}
                    >
                      {active && <span className={styles['branding-anchor-dot']} />}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className={styles['branding-config-group']}>
              <span className={styles['branding-config-label']}>Size</span>
              <div className={styles['branding-size-row']}>
                {SIZE_OPTIONS.map((s) => {
                  const active = configDraft.size === s;
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setConfigDraft({ ...configDraft, size: s })}
                      className={active ? styles['branding-size-btn-active'] : styles['branding-size-btn']}
                      aria-pressed={active}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>

              <span className={styles['branding-config-label']} style={{ marginTop: '0.75rem' }}>Transparency</span>
              <div className={styles['branding-opacity-row']}>
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={1}
                  value={Math.round(configDraft.opacity * 100)}
                  onChange={(e) => setConfigDraft({ ...configDraft, opacity: Number(e.target.value) / 100 })}
                  className={styles['branding-opacity-slider']}
                  aria-label="Opacity"
                />
                <span className={styles['branding-opacity-value']}>
                  {Math.round(configDraft.opacity * 100)}%
                </span>
              </div>
            </div>
          </div>
        )}

        <div className={styles['branding-save-row']}>
          {isConfigDirty && (
            <span className={styles['branding-dirty-hint']}>Unsaved changes</span>
          )}
          <Button
            variant="ghost"
            size="md"
            onClick={handleResetConfig}
            disabled={!isConfigDirty || savingConfig}
          >
            Reset
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={handleSaveConfig}
            loading={savingConfig}
            disabled={!isConfigDirty || savingConfig}
          >
            Save Config
          </Button>
        </div>
      </section>

      {/* Reprocess section */}
      <section className={`${styles['card-clay-lg']} ${styles['branding-card']}`}>
        <div className={styles['branding-header-row']}>
          <div>
            <h3 className={styles['branding-title']}>Reprocess Existing Albums</h3>
            <p className={styles['branding-subtitle']}>
              Uploading a new logo or changing its position/size/opacity does not
              automatically re-composite photos that were already processed. Use
              this to pick affected albums and re-run the Lambda compositor on them.
            </p>
          </div>
          <Button variant="primary" size="md" onClick={() => setReprocessOpen(true)}>
            <RefreshCw className={styles['tab-icon']} />
            Reprocess Albums
          </Button>
        </div>
      </section>

      <ReprocessAlbumsModal
        isOpen={reprocessOpen}
        onClose={() => setReprocessOpen(false)}
        onReprocessed={loadData}
      />
    </div>
  );
};

// ─── TestingTab ──────────────────────────────────────────────────────────────

const TestingTab: React.FC = () => {
  // WhatsApp state
  const [phone, setPhone] = useState('');
  const [waName, setWaName] = useState('');
  const [waSending, setWaSending] = useState<null | 'approved' | 'rejected'>(null);
  const [waResult, setWaResult] = useState<
    | { ok: true; kind: 'approved' | 'rejected'; sid: string; status: string }
    | { ok: false; kind: 'approved' | 'rejected'; error: string; code?: number }
    | null
  >(null);

  // Email state
  const [email, setEmail] = useState('');
  const [emailName, setEmailName] = useState('');
  const [emailSending, setEmailSending] = useState<null | 'approved' | 'rejected'>(null);
  const [emailResult, setEmailResult] = useState<
    | { ok: true; kind: 'approved' | 'rejected'; id: string }
    | { ok: false; kind: 'approved' | 'rejected'; error: string }
    | null
  >(null);

  const phoneValid = /^\+[1-9]\d{7,14}$/.test(phone.trim());
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const handleWaSend = async (kind: 'approved' | 'rejected') => {
    if (!phoneValid || waSending) return;
    setWaSending(kind);
    setWaResult(null);
    try {
      const res = await adminApi.testWhatsappTemplate({
        kind,
        phone: phone.trim(),
        name: waName.trim() || undefined,
      });
      setWaResult({ ok: true, kind, sid: res.sid, status: res.status });
      toast.success(`Test ${kind} message queued (${res.status})`);
    } catch (err) {
      const e = err as { response?: { data?: { error?: string; code?: number } } };
      const errorMsg = e.response?.data?.error || 'Failed to send test message';
      setWaResult({ ok: false, kind, error: errorMsg, code: e.response?.data?.code });
      toast.error(errorMsg);
    } finally {
      setWaSending(null);
    }
  };

  const handleEmailSend = async (kind: 'approved' | 'rejected') => {
    if (!emailValid || emailSending) return;
    setEmailSending(kind);
    setEmailResult(null);
    try {
      const res = await adminApi.testEmailTemplate({
        kind,
        email: email.trim(),
        name: emailName.trim() || undefined,
      });
      setEmailResult({ ok: true, kind, id: res.id });
      toast.success(`Test ${kind} email sent`);
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      const errorMsg = e.response?.data?.error || 'Failed to send test email';
      setEmailResult({ ok: false, kind, error: errorMsg });
      toast.error(errorMsg);
    } finally {
      setEmailSending(null);
    }
  };

  return (
    <div className={styles['space-y-6']}>
      <div className={styles['branding-info-box']}>
        <Info className={styles['branding-info-icon']} />
        <span>
          Send the real beta approval or rejection templates to any recipient for testing.
          Uses the same flows as the production approval pipeline.
        </span>
      </div>

      {/* WhatsApp Test Card */}
      <section className={`${styles['card-clay-lg']} ${styles['branding-card']}`}>
        <div className={styles['branding-header-row']}>
          <div>
            <h3 className={styles['branding-title']}>Twilio WhatsApp Test Send</h3>
            <p className={styles['branding-subtitle']}>
              Triggers the same template flow used when an admin approves or rejects a
              waitlist entry. Recipient must have opted in to WhatsApp messages from your
              sender. Phone must be in E.164 format (e.g. <code>+919876543210</code>).
            </p>
          </div>
        </div>

        <div className={styles['branding-config-grid']}>
          <div className={styles['branding-config-group']}>
            <span className={styles['branding-config-label']}>Phone (E.164)</span>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+919876543210"
              className={styles['reprocess-search']}
              autoComplete="off"
            />
            {phone && !phoneValid && (
              <span className={styles['branding-dirty-hint']}>
                Must start with + and country code
              </span>
            )}
          </div>

          <div className={styles['branding-config-group']}>
            <span className={styles['branding-config-label']}>Recipient name (optional)</span>
            <input
              type="text"
              value={waName}
              onChange={(e) => setWaName(e.target.value)}
              placeholder="there"
              className={styles['reprocess-search']}
              autoComplete="off"
            />
          </div>
        </div>

        <div className={styles['branding-save-row']}>
          <Button
            variant="ghost"
            size="md"
            onClick={() => handleWaSend('rejected')}
            loading={waSending === 'rejected'}
            disabled={!phoneValid || waSending !== null}
          >
            <Send className={styles['tab-icon']} />
            Test Rejection
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={() => handleWaSend('approved')}
            loading={waSending === 'approved'}
            disabled={!phoneValid || waSending !== null}
          >
            <Send className={styles['tab-icon']} />
            Test Approval
          </Button>
        </div>

        {waResult && (
          <div
            className={styles['branding-info-box']}
            style={{
              borderColor: waResult.ok ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)',
              background: waResult.ok ? 'rgba(34, 197, 94, 0.08)' : 'rgba(239, 68, 68, 0.08)',
            }}
          >
            {waResult.ok ? (
              <Check className={styles['branding-info-icon']} style={{ color: '#16a34a' }} />
            ) : (
              <Info className={styles['branding-info-icon']} />
            )}
            <span>
              {waResult.ok ? (
                <>
                  <strong>{waResult.kind === 'approved' ? 'Approval' : 'Rejection'}</strong>{' '}
                  template accepted by Twilio. SID: <code>{waResult.sid}</code> · status:{' '}
                  <strong>{waResult.status}</strong>
                </>
              ) : (
                <>
                  <strong>Failed</strong> to send{' '}
                  {waResult.kind === 'approved' ? 'approval' : 'rejection'} template:{' '}
                  {waResult.error}
                  {waResult.code != null && <> (code {waResult.code})</>}
                </>
              )}
            </span>
          </div>
        )}
      </section>

      {/* Email Test Card */}
      <section className={`${styles['card-clay-lg']} ${styles['branding-card']}`}>
        <div className={styles['branding-header-row']}>
          <div>
            <h3 className={styles['branding-title']}>
              <Mail className={styles['tab-icon']} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 8 }} />
              Email Test Send
            </h3>
            <p className={styles['branding-subtitle']}>
              Send the beta approval or rejection email to any address. Uses the same
              Resend templates as the production approval flow. Emails come from{' '}
              <code>team@pixelmachha.com</code>.
            </p>
          </div>
        </div>

        <div className={styles['branding-config-grid']}>
          <div className={styles['branding-config-group']}>
            <span className={styles['branding-config-label']}>Email address</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@example.com"
              className={styles['reprocess-search']}
              autoComplete="off"
            />
            {email && !emailValid && (
              <span className={styles['branding-dirty-hint']}>
                Enter a valid email address
              </span>
            )}
          </div>

          <div className={styles['branding-config-group']}>
            <span className={styles['branding-config-label']}>Recipient name (optional)</span>
            <input
              type="text"
              value={emailName}
              onChange={(e) => setEmailName(e.target.value)}
              placeholder="there"
              className={styles['reprocess-search']}
              autoComplete="off"
            />
          </div>
        </div>

        <div className={styles['branding-save-row']}>
          <Button
            variant="ghost"
            size="md"
            onClick={() => handleEmailSend('rejected')}
            loading={emailSending === 'rejected'}
            disabled={!emailValid || emailSending !== null}
          >
            <Mail className={styles['tab-icon']} />
            Test Rejection
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={() => handleEmailSend('approved')}
            loading={emailSending === 'approved'}
            disabled={!emailValid || emailSending !== null}
          >
            <Mail className={styles['tab-icon']} />
            Test Approval
          </Button>
        </div>

        {emailResult && (
          <div
            className={styles['branding-info-box']}
            style={{
              borderColor: emailResult.ok ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)',
              background: emailResult.ok ? 'rgba(34, 197, 94, 0.08)' : 'rgba(239, 68, 68, 0.08)',
            }}
          >
            {emailResult.ok ? (
              <Check className={styles['branding-info-icon']} style={{ color: '#16a34a' }} />
            ) : (
              <Info className={styles['branding-info-icon']} />
            )}
            <span>
              {emailResult.ok ? (
                <>
                  <strong>{emailResult.kind === 'approved' ? 'Approval' : 'Rejection'}</strong>{' '}
                  email sent via Resend. ID: <code>{emailResult.id}</code>
                </>
              ) : (
                <>
                  <strong>Failed</strong> to send{' '}
                  {emailResult.kind === 'approved' ? 'approval' : 'rejection'} email:{' '}
                  {emailResult.error}
                </>
              )}
            </span>
          </div>
        )}
      </section>
    </div>
  );
};

// ─── CommunicationsTab ──────────────────────────────────────────────────────

const CommunicationsTab: React.FC = () => {
  const [entries, setEntries] = useState<AdminWaitlistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [sending, setSending] = useState(false);
  const [filter, setFilter] = useState<'all' | 'unsent' | 'sent'>('unsent');
  const [search, setSearch] = useState('');

  const loadEntries = useCallback(() => {
    setLoading(true);
    adminApi.listWaitlist().then(setEntries).finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  const filtered = useMemo(() => {
    let list = entries;
    if (filter === 'unsent') list = list.filter((e) => !e.communicationSentAt);
    else if (filter === 'sent') list = list.filter((e) => !!e.communicationSentAt);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (e) =>
          e.user.name.toLowerCase().includes(q) ||
          e.user.email.toLowerCase().includes(q),
      );
    }
    return list;
  }, [entries, filter, search]);

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((e) => e._id)));
    }
  };

  const handleSend = async () => {
    if (selected.size === 0 || sending) return;
    setSending(true);
    try {
      const res = await adminApi.sendBulkSignupEmails(Array.from(selected));
      toast.success(`Sent ${res.sent} email${res.sent !== 1 ? 's' : ''}${res.failed > 0 ? `, ${res.failed} failed` : ''}`);
      if (res.failed > 0) {
        const failedEntries = res.results.filter((r) => !r.success);
        failedEntries.forEach((r) => toast.error(`${r.email}: ${r.error}`));
      }
      setSelected(new Set());
      loadEntries();
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error || 'Failed to send emails');
    } finally {
      setSending(false);
    }
  };

  const allSelected = filtered.length > 0 && selected.size === filtered.length;

  return (
    <div className={styles['space-y-6']}>
      <div className={styles['branding-info-box']}>
        <Info className={styles['branding-info-icon']} />
        <span>
          Send &quot;beta request received&quot; acknowledgment emails to waitlist users.
          Select users and send in bulk. Users who have already received the email are
          marked and can be filtered out.
        </span>
      </div>

      {/* Toolbar */}
      <section className={`${styles['card-clay-lg']} ${styles['branding-card']}`}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email..."
            className={styles['reprocess-search']}
            style={{ flex: 1, minWidth: 200 }}
          />
          <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
            {(['unsent', 'all', 'sent'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                style={{
                  padding: '6px 14px',
                  fontSize: 13,
                  fontWeight: 500,
                  borderRadius: 8,
                  border: '1px solid',
                  borderColor: filter === f ? 'rgba(239, 68, 68, 0.4)' : 'var(--border-subtle, rgba(255,255,255,0.08))',
                  background: filter === f ? 'rgba(239, 68, 68, 0.12)' : 'transparent',
                  color: filter === f ? '#ef4444' : 'var(--text-secondary, rgba(255,255,255,0.6))',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.15s',
                }}
              >
                {f === 'unsent' ? 'Not Sent' : f === 'sent' ? 'Sent' : 'All'}
              </button>
            ))}
          </div>
        </div>

        {/* Select all + send bar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
            <input
              type="checkbox"
              checked={allSelected}
              onChange={toggleAll}
              style={{ width: 16, height: 16, accentColor: '#ef4444' }}
            />
            <span className={styles['branding-config-label']} style={{ margin: 0 }}>
              Select all ({filtered.length})
            </span>
          </label>
          <Button
            variant="primary"
            size="md"
            onClick={handleSend}
            loading={sending}
            disabled={selected.size === 0 || sending}
          >
            <Mail className={styles['tab-icon']} />
            Send to {selected.size} user{selected.size !== 1 ? 's' : ''}
          </Button>
        </div>

        {/* User list */}
        {loading ? (
          <div className={styles['loading-wrapper']}>
            <div className={styles['loading-spinner']} />
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px 0', opacity: 0.5 }}>
            <p className={styles['branding-subtitle']}>
              {filter === 'unsent' ? 'All users have been contacted' : 'No matching users'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {filtered.map((entry) => (
              <label
                key={entry._id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 14px',
                  borderRadius: 12,
                  cursor: 'pointer',
                  background: selected.has(entry._id) ? 'rgba(239, 68, 68, 0.06)' : 'transparent',
                  border: '1px solid',
                  borderColor: selected.has(entry._id) ? 'rgba(239, 68, 68, 0.2)' : 'var(--border-subtle, rgba(0,0,0,0.06))',
                  transition: 'background 0.15s, border-color 0.15s',
                }}
              >
                <input
                  type="checkbox"
                  checked={selected.has(entry._id)}
                  onChange={() => toggleSelect(entry._id)}
                  style={{ width: 16, height: 16, accentColor: '#ef4444', flexShrink: 0 }}
                />
                {entry.user.picture ? (
                  <img
                    src={entry.user.picture}
                    alt=""
                    referrerPolicy="no-referrer"
                    style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                  />
                ) : (
                  <div
                    style={{
                      width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                      background: 'rgba(239, 68, 68, 0.1)', display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                      fontSize: 14, fontWeight: 600, color: '#ef4444',
                    }}
                  >
                    {entry.user.name.charAt(0).toUpperCase()}
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {entry.user.name}
                  </div>
                  <div style={{ fontSize: 12, opacity: 0.6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {entry.user.email}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  <Badge variant={entry.status === 'approved' ? 'green' : entry.status === 'rejected' ? 'red' : 'yellow'}>
                    {entry.status}
                  </Badge>
                  {entry.communicationSentAt ? (
                    <span style={{ fontSize: 11, color: '#16a34a', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Check style={{ width: 14, height: 14 }} />
                      Sent {formatDate(entry.communicationSentAt)}
                    </span>
                  ) : (
                    <span style={{ fontSize: 11, opacity: 0.4 }}>Not sent</span>
                  )}
                </div>
              </label>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

// ─── UploadErrorsTab ─────────────────────────────────────────────────────────

const ERROR_LABELS: Record<AdminUploadError['errorCode'], string> = {
  size_exceeded: 'Too large',
  invalid_file: 'Invalid file',
  quota_exceeded: 'Quota reached',
  processing_error: 'Processing failed',
};

const UploadErrorsTab: React.FC = () => {
  const [items, setItems] = useState<AdminUploadError[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);

  const load = useCallback((p: number) => {
    setLoading(true);
    adminApi.listUploadErrors(p)
      .then((res) => {
        setItems(res.items);
        setPages(res.pages);
        setTotal(res.total);
        setPage(p);
      })
      .catch(() => toast.error('Failed to load upload errors'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(1); }, [load]);

  return (
    <div>
      <div className={styles['section-header']}>
        <h2 className={styles['section-title']}>FTP Upload Failures</h2>
        <p className={styles['section-subtitle']}>
          {total > 0 ? `${total} failed upload${total === 1 ? '' : 's'} across all events` : 'All uploads processed cleanly'}
        </p>
      </div>

      {loading ? (
        <div className={styles['empty-state']}>
          <p className={styles['empty-text']}>Loading…</p>
        </div>
      ) : items.length === 0 ? (
        <div className={styles['empty-state']}>
          <CheckCircle2 style={{ width: '2rem', height: '2rem', color: '#22c55e', marginBottom: '0.5rem' }} />
          <p className={styles['empty-text']}>No upload failures recorded.</p>
        </div>
      ) : (
        <>
          <div className={styles['table-wrapper']}>
            <table className={styles['table']}>
              <thead>
                <tr>
                  <th className={styles['th']}>File</th>
                  <th className={styles['th']}>Error</th>
                  <th className={styles['th']}>Event</th>
                  <th className={styles['th']}>User</th>
                  <th className={styles['th']}>When</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item._id} className={styles['tr']}>
                    <td className={styles['td']}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <FileX style={{ width: '1rem', height: '1rem', color: 'rgba(239,68,68,0.7)', flexShrink: 0 }} />
                        <span style={{ fontWeight: 500, fontSize: '0.875rem', wordBreak: 'break-all' }}>{item.filename}</span>
                      </div>
                      <p style={{ margin: '0.2rem 0 0 1.5rem', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{item.errorMessage}</p>
                    </td>
                    <td className={styles['td']}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center',
                        padding: '0.125rem 0.5rem', borderRadius: '9999px',
                        background: 'rgba(239,68,68,0.1)', color: 'rgba(239,68,68,1)',
                        fontSize: '0.6875rem', fontWeight: 600,
                      }}>
                        {ERROR_LABELS[item.errorCode]}
                      </span>
                    </td>
                    <td className={styles['td']}>
                      {item.eventName ? (
                        <div>
                          <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 500 }}>{item.eventName}</p>
                          <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{item.eventStatus}</p>
                        </div>
                      ) : <span style={{ color: 'var(--text-tertiary)' }}>—</span>}
                    </td>
                    <td className={styles['td']}>
                      {item.userName ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          {item.userPicture && (
                            <img src={item.userPicture} alt="" style={{ width: '1.75rem', height: '1.75rem', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                          )}
                          <div>
                            <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 500 }}>{item.userName}</p>
                            <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{item.userEmail}</p>
                          </div>
                        </div>
                      ) : <span style={{ color: 'var(--text-tertiary)' }}>—</span>}
                    </td>
                    <td className={styles['td']} style={{ whiteSpace: 'nowrap', color: 'var(--text-secondary)', fontSize: '0.8125rem' }}>
                      {formatRelative(new Date(item.failedAt))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.75rem', marginTop: '1.5rem' }}>
              <Button variant="secondary" onClick={() => load(page - 1)} disabled={page <= 1 || loading}>Prev</Button>
              <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Page {page} of {pages}</span>
              <Button variant="secondary" onClick={() => load(page + 1)} disabled={page >= pages || loading}>Next</Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ─── AdminPage ───────────────────────────────────────────────────────────────

export const AdminPage: React.FC = () => {
  usePageTitle('Admin');
  const [tab, setTab] = useState<'users' | 'waitlist' | 'quotas' | 'costs' | 'twilio' | 'security' | 'branding' | 'comms' | 'testing' | 'upload-errors' | 'devtools'>('users');
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [pendingCount, setPendingCount] = useState<number | null>(null);
  const [quotaPendingCount, setQuotaPendingCount] = useState<number | null>(null);
  const [profileUser, setProfileUser] = useState<AdminUser | null>(null);

  const refreshUsers = useCallback(() => {
    setLoading(true);
    adminApi.listUsers().then(setUsers).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    refreshUsers();
  }, [refreshUsers]);

  const totalEvents = users.reduce((s, u) => s + u.eventCount, 0);
  const betaCount = users.filter((u) => u.betaApproved).length;
  const membersCount = users.filter((u) => u.albumCount > 0).length;

  return (
    <div className={styles['page']}>
      {/* Header */}
      <motion.div
        className={styles['page-header']}
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
      >
        <h1 className={styles['page-title']}>Admin Panel</h1>
        <p className={styles['page-subtitle']}>All users and their activity</p>
      </motion.div>

      {/* Tab switcher */}
      <div className={styles['admin-layout']}>
      <div className={styles['tab-bar']}>
        {(['users', 'waitlist', 'quotas', 'costs', 'twilio', 'security', 'branding', 'comms', 'testing', 'upload-errors', 'devtools'] as const).map((t) => {
          const label = t === 'users' ? 'Users' : t === 'waitlist' ? 'Waitlist' : t === 'quotas' ? 'Quotas' : t === 'costs' ? 'Costs' : t === 'twilio' ? 'Twilio' : t === 'security' ? 'Security' : t === 'branding' ? 'Branding' : t === 'comms' ? 'Comms' : t === 'testing' ? 'Testing' : t === 'upload-errors' ? 'Upload Errors' : 'DevTools';
          return (
            <motion.button
              key={t}
              onClick={() => setTab(t)}
              className={tab === t ? styles['tab-btn-active'] : styles['tab-btn']}
              whileTap={{ scale: 0.95 }}
            >
              {tab === t && (
                <motion.div
                  layoutId="admin-tab-indicator"
                  className={styles['tab-indicator']}
                  transition={{ type: 'spring', stiffness: 500, damping: 40 }}
                />
              )}
              {t === 'users' && <Users className={styles['tab-icon']} />}
              {t === 'waitlist' && <ClipboardList className={styles['tab-icon']} />}
              {t === 'quotas' && <Gauge className={styles['tab-icon']} />}
              {t === 'costs' && <IndianRupee className={styles['tab-icon']} />}
              {t === 'twilio' && <MessageCircle className={styles['tab-icon']} />}
              {t === 'security' && <Shield className={styles['tab-icon']} />}
              {t === 'branding' && <Palette className={styles['tab-icon']} />}
              {t === 'comms' && <Mail className={styles['tab-icon']} />}
              {t === 'testing' && <FlaskConical className={styles['tab-icon']} />}
              {t === 'upload-errors' && <FileX className={styles['tab-icon']} />}
              {t === 'devtools' && <ShieldAlert className={styles['tab-icon']} />}
              {label}
              {t === 'waitlist' && pendingCount !== null && pendingCount > 0 && (
                <span className={styles['tab-badge']}>
                  {pendingCount}
                </span>
              )}
              {t === 'quotas' && quotaPendingCount !== null && quotaPendingCount > 0 && (
                <span className={styles['tab-badge']}>
                  {quotaPendingCount}
                </span>
              )}
            </motion.button>
          );
        })}
      </div>

      <div className={styles['tab-content']}>
      {tab === 'users' ? (
        <>
          {!loading && (
            <motion.div
              className={styles['stats-grid']}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <div className={`${styles['card-clay']} ${styles['stat-card']}`}>
                <div className={styles['stat-icon-box-orange']}>
                  <Users className={`${styles['stat-icon']} ${styles['icon-orange']}`} />
                </div>
                <div>
                  <p className={styles['stat-value']}>{users.length}</p>
                  <p className={styles['stat-label']}>Total users</p>
                </div>
              </div>
              <div className={`${styles['card-clay']} ${styles['stat-card']}`}>
                <div className={styles['stat-icon-box-rose']}>
                  <Calendar className={`${styles['stat-icon']} ${styles['icon-rose']}`} />
                </div>
                <div>
                  <p className={styles['stat-value']}>{totalEvents}</p>
                  <p className={styles['stat-label']}>Total events</p>
                </div>
              </div>
              <div className={`${styles['card-clay']} ${styles['stat-card']}`}>
                <div className={styles['stat-icon-box-orange']}>
                  <CheckCircle2 className={`${styles['stat-icon']} ${styles['icon-orange']}`} />
                </div>
                <div>
                  <p className={styles['stat-value']}>{betaCount}</p>
                  <p className={styles['stat-label']}>Beta access</p>
                </div>
              </div>
              <div className={`${styles['card-clay']} ${styles['stat-card']}`}>
                <div className={styles['stat-icon-box-rose']}>
                  <Share2 className={`${styles['stat-icon']} ${styles['icon-rose']}`} />
                </div>
                <div>
                  <p className={styles['stat-value']}>{membersCount}</p>
                  <p className={styles['stat-label']}>Album members</p>
                </div>
              </div>
            </motion.div>
          )}

          {loading ? (
            <div className={styles['loading-wrapper']}>
              <motion.p
                className={styles['loading-text']}
                animate={{ opacity: [0.4, 1, 0.4] }}
                transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
              >
                Loading users…
              </motion.p>
              <motion.div
                className={styles['loading-mascot']}
                initial={{ y: 40, opacity: 0 }}
                animate={{ y: [6, -4, 6], opacity: 1 }}
                transition={{ y: { repeat: Infinity, duration: 2.4, ease: 'easeInOut' }, opacity: { duration: 0.4 } }}
              >
                <MachhaCharacter state="Loading" part="face" size={100} />
              </motion.div>
            </div>
          ) : users.length === 0 ? (
            <div className={styles['users-empty-wrapper']}>
              <p className={styles['users-empty-text']}>
                No users found.
              </p>
              <motion.div
                className={styles['users-empty-mascot']}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1, rotate: [10, 16, 10] }}
                transition={{ rotate: { repeat: Infinity, duration: 3.5, ease: 'easeInOut' }, default: { duration: 0.4 } }}
              >
                <MachhaCharacter state="Empty" part="face" size={90} />
              </motion.div>
            </div>
          ) : (
            <motion.div className={styles['users-list']} variants={clayStaggerContainer} initial="hidden" animate="visible">
              {users.map((user) => (
                <UserRow key={user._id} user={user} onAvatarClick={() => setProfileUser(user)} onDeleted={refreshUsers} />
              ))}
            </motion.div>
          )}
        </>
      ) : tab === 'waitlist' ? (
        <WaitlistTab onLoad={setPendingCount} />
      ) : tab === 'quotas' ? (
        <QuotasTab onLoad={setQuotaPendingCount} />
      ) : tab === 'costs' ? (
        <CostsTab />
      ) : tab === 'twilio' ? (
        <TwilioTab />
      ) : tab === 'security' ? (
        <SecurityTab />
      ) : tab === 'branding' ? (
        <BrandingTab />
      ) : tab === 'comms' ? (
        <CommunicationsTab />
      ) : tab === 'testing' ? (
        <TestingTab />
      ) : tab === 'upload-errors' ? (
        <UploadErrorsTab />
      ) : (
        <DevToolsTab />
      )}
      </div>
      </div>

      <UserProfileModal user={profileUser} isOpen={!!profileUser} onClose={() => setProfileUser(null)} />
    </div>
  );
};
